import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import { canRegisterShipmentPrices } from '../../../utils/shipment-price-access';
import ShipmentDetailsModal from '../../../components/shipments/ShipmentDetailsModal';
import { shipmentHeaderFields } from '../../../utils/shipment-header-form';
import { useShipmentDetails } from '../../../hooks/useShipmentDetails';
import {
  countryCodeToFlag,
  getTrackingEventLabel,
  trackingCountryCode,
  normalizeUnLocode,
} from '../../../utils/shipment-tracking-display';
import { Icon } from '@iconify/react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import * as XLSX from 'xlsx';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Spinner,
  Pagination,
  Input,
  useDisclosure,
  Tooltip,
  Chip,
  Card,
  CardBody,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import {
  getExporterDivisions,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import {
  Shipment,
  ShipmentBox,
  BoxFormatMapping,
  ShipmentTrackingSummary,
} from 'common/src/utils/types';
import {
  buildLiquidatedDetailIndex,
  buildLiquidatedDetailColumns,
} from '../../../utils/shipment-liquidated-export';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import ExcelUploadModal from '../../../components/shipments/excel-upload-modal';
import { synchronizeFruticShipments } from 'common/src/api/frutic';
import {
  getShipmentReconciliationCases,
  type ShipmentReplacement,
} from 'common/src/api/shipments';
import {
  formatDate,
  getDaysSinceArrival,
  formatNumber,
} from '../../../utils/functions';
import {
  arrivedTrackingEvents,
  compareShipmentsByField,
  compareShipmentsByOperationalPriority,
  getPlannedShipmentStatus,
  getShipmentCalendarDayOffset,
  getShipmentLogisticsStatus,
  inTransitTrackingEvents,
  ShipmentManualSortField,
  ShipmentSortDirection,
} from '../../../utils/shipment-logistics';
import {
  addUniqueShipmentIdentifier,
  normalizeShipmentIdentifier,
} from '../../../utils/shipment-identifiers';
import { getWeekKey, sortWeekKeys } from 'common/src/utils/functions';
import { createInFlightRequestCache } from 'common/src/utils/in-flight-request';
import { countShipmentBoxRows } from 'common/src/utils/shipment-overview';
import { VALID_FORMATS } from 'common/src/utils/definitions';
import { exporterUsesFrutic } from 'common/src/utils/frutic-functions';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import {
  getShipmentBoxesForExport,
  getShipmentsDetailsPage,
  deleteShipmentsByPacking,
  deleteShipment,
} from 'common/src/api/shipments';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import Header from '../../../components/layouts/Header';
import {
  FiltersToggle,
  LoadingState,
  MapFormatsButton,
  PageLayout,
} from '../../../components/ui';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useShipmentsStore, {
  type ShipmentRecord,
} from '../../../store/useShipmentsStore';
import useFruticStore from '../../../store/useFruticStore';
import useReceiverStore from '../../../store/useReceiverStore';
import ShipmentDistributionRow from './ShipmentDistributionRow';
import { compareShipmentCalibersAscending } from '../../../utils/caliber-order';
import {
  getShipmentDistribution,
  filterShipmentDimensions,
  shipmentDimensionKey,
  type ShipmentDimensionFilter,
} from '../../../utils/shipment-summary';
import { ReceiverMappingSection } from '../../../components/receiver-mapping/ReceiverMapping';
import FormatMappingModal from '../../../components/frutic-box-mapping/FormatMappingModal';

type GroupedShipment = {
  id: string;
  shipment_id: number;
  reference_number: string;
  division_id?: number | null;
  division_name?: string | null;
  receiver?: string;
  receiver_names?: string[];
  packing_names?: string[];
  has_unmapped_receiver?: boolean;
  exporter_receiver_id?: number | null;
  business_type?: string | null;
  sale_order?: string | null;
  original_incoterm?: string | null;
  total_quantity: number;
  total_kilos: number;
  ship_name: string;
  ship_etd: string;
  ship_eta: string;
  ship_destination_country?: string | null;
  ship_destination_port?: string | null;
  estimated_settlement_date: Date | '';
  status: 'settled' | 'pending';
  packing_name: string;
  container_numbers?: string[];
  awb_numbers?: string[];
  tracking_journeys?: ShipmentTrackingSummary[];
};

type ChartSourceCoverage = {
  totalReferences: number;
  totalValue: number;
  apiSourcedReferences: number;
  apiSourcedValue: number;
  canonicalSourcedReferences: number;
  missingReferences: number;
};

type ChartDataSource = 'api' | 'canonical' | 'missing' | null;
type ChartSourceMode = 'effective' | 'canonical';

const getChartSourceCoverage = (
  shipments: GroupedShipment[],
  getValue: (shipment: GroupedShipment) => number,
  getSource: (shipment: GroupedShipment) => ChartDataSource
): ChartSourceCoverage =>
  shipments.reduce<ChartSourceCoverage>(
    (coverage, shipment) => {
      const source = getSource(shipment);
      if (source === null) return coverage;

      const value = getValue(shipment);
      coverage.totalReferences += 1;
      coverage.totalValue += value;
      if (source === 'api') {
        coverage.apiSourcedReferences += 1;
        coverage.apiSourcedValue += value;
      } else if (source === 'canonical') {
        coverage.canonicalSourcedReferences += 1;
      } else {
        coverage.missingReferences += 1;
      }
      return coverage;
    },
    {
      totalReferences: 0,
      totalValue: 0,
      apiSourcedReferences: 0,
      apiSourcedValue: 0,
      canonicalSourcedReferences: 0,
      missingReferences: 0,
    }
  );

const SHIPMENT_WITHOUT_REFERENCE = 'SIN REFERENCIA';
type SettlementStatusFilter = 'all' | 'settled' | 'pending' | 'overdue';
type LogisticsStatusFilter =
  | 'all'
  | 'pendingDeparture'
  | 'inTransit'
  | 'arrived'
  | 'missingEta';
type ApiTrackingFilter = 'all' | 'tracked' | 'untracked';

type ShipmentSortSelection = {
  field: ShipmentManualSortField | null;
  direction: ShipmentSortDirection;
};

type ShipmentSortOption = {
  field: ShipmentManualSortField;
  label: string;
};

const shipmentSortDefaultDirections: Record<
  ShipmentManualSortField,
  ShipmentSortDirection
> = {
  client: 'ascending',
  reference: 'ascending',
  container: 'ascending',
  kilos: 'descending',
  logisticsStatus: 'ascending',
  etd: 'ascending',
  eta: 'ascending',
  vessel: 'ascending',
  destination: 'ascending',
  apiTracking: 'descending',
  settlementStatus: 'ascending',
  settlementDate: 'ascending',
};

const ShipmentSortableHeader = ({
  label,
  options,
  sort,
  operationalOrderLabel,
  menuLabel,
  onSelect,
}: {
  label: string;
  options: ShipmentSortOption[];
  sort: ShipmentSortSelection;
  operationalOrderLabel: string;
  menuLabel: string;
  onSelect: (field: ShipmentManualSortField | null) => void;
}) => {
  const activeOption = options.find((option) => option.field === sort.field);
  const items = [
    { key: 'operational', label: operationalOrderLabel },
    ...options.map((option) => ({ key: option.field, label: option.label })),
  ];

  return (
    <Dropdown>
      <DropdownTrigger>
        <button
          type='button'
          className='flex w-full cursor-pointer items-center justify-between gap-2 rounded-md py-1 text-left outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary'
          aria-label={`${menuLabel}: ${label}`}
        >
          <span className='flex min-w-0 flex-col'>
            <span>{label}</span>
            {activeOption ? (
              <span className='truncate text-[10px] font-medium normal-case tracking-normal text-primary'>
                {activeOption.label}{' '}
                {sort.direction === 'ascending' ? '↑' : '↓'}
              </span>
            ) : null}
          </span>
          <Icon
            icon='tabler:chevron-down'
            className='shrink-0 text-[10px] text-default-400'
          />
        </button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={`${menuLabel}: ${label}`}
        items={items}
        selectionMode='single'
        selectedKeys={new Set([sort.field || 'operational'])}
        onAction={(key) =>
          onSelect(
            key === 'operational' ? null : (key as ShipmentManualSortField)
          )
        }
      >
        {(item) => (
          <DropdownItem
            key={item.key}
            endContent={
              item.key === sort.field ? (
                <span className='text-sm text-primary'>
                  {sort.direction === 'ascending' ? '↑' : '↓'}
                </span>
              ) : null
            }
          >
            {item.label}
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

const normalizeShipmentReference = (reference?: string | null) => {
  const trimmed = reference?.trim();
  return trimmed ? trimmed : SHIPMENT_WITHOUT_REFERENCE;
};

const getCountryDisplayName = (countryCode: string, locale: string) => {
  try {
    const displayName =
      new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode) ||
      countryCode;
    return displayName.toLocaleUpperCase(locale);
  } catch {
    return countryCode;
  }
};

const getShipmentSettlementStatus = (
  group: GroupedShipment
): Exclude<SettlementStatusFilter, 'all'> => {
  const isOverdue =
    group.status === 'pending' &&
    group.estimated_settlement_date &&
    new Date(group.estimated_settlement_date) < new Date();

  if (group.status === 'settled') return 'settled';
  return isOverdue ? 'overdue' : 'pending';
};

const SegmentedToggle = <T extends string>({
  value,
  options,
  onChange,
  compact = false,
}: {
  value: T;
  options: Array<{ value: T; label: string; shortLabel?: string }>;
  compact?: boolean;
  onChange: (value: T) => void;
}) => (
  <div
    className={`grid rounded-lg bg-default-100 ${compact ? 'p-0.5' : 'p-1'}`}
    style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
  >
    {options.map((option) => (
      <Button
        key={option.value}
        size='sm'
        radius='sm'
        aria-label={option.label}
        aria-pressed={value === option.value}
        title={option.label}
        className={
          compact
            ? `h-6 min-w-0 px-2 text-xs ${value === option.value ? 'bg-default-200 font-semibold text-default-900' : 'text-default-500'}`
            : undefined
        }
        color={!compact && value === option.value ? 'primary' : 'default'}
        variant={value === option.value ? 'solid' : 'light'}
        onPress={() => onChange(option.value)}
      >
        {compact ? option.shortLabel || option.label : option.label}
      </Button>
    ))}
  </div>
);

type ValidBoxFormat = (typeof VALID_FORMATS)[number];

const normalizeBoxValue = (value: string | null | undefined) =>
  (value || '').trim().toUpperCase();

const isValidBoxFormat = (
  value: string | null | undefined
): value is ValidBoxFormat =>
  VALID_FORMATS.includes(normalizeBoxValue(value) as ValidBoxFormat);

const runShipmentPageRequest = createInFlightRequestCache();

const ShipmentsPage = ({ viewMode }: { viewMode: 'detalle' | 'resumen' }) => {
  const { t, currentLanguage } = useTranslation('shipments');
  const userProfile = useUserStore((state) => state.userProfile);
  const canRegisterPrices = canRegisterShipmentPrices(userProfile);
  const darkMode = useUserStore((state) => state.darkMode);
  // Supabase may replace the Session object when a background tab regains
  // focus. Subscribe to the stable user id so token rotation does not reload
  // the whole shipments page.
  const authenticatedUserId = useUserStore((state) => state.session?.user.id);
  const { selectedSeason } = useFilterStore();
  const usesFrutic = exporterUsesFrutic(userProfile?.exporter, selectedSeason);
  const userProfileContextKey = userProfile
    ? [
        userProfile.id,
        userProfile.exporter?.id ?? userProfile.exporter_id,
        usesFrutic ? 'frutic' : 'local',
      ].join(':')
    : null;
  const { selectedSpecies } = useDataStore();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDemoModalOpen, onClose: onDemoModalClose } =
    useDisclosure();
  const { shipments, loading, setShipments, setLoading, setError } =
    useShipmentsStore();
  const { fetchMappings, mappedFormats } = useFruticStore();
  const {
    unmappedReceivers,
    exporterReceivers,
    fetchExporterReceivers,
    fetchReceiverMappings,
    addExporterReceiver,
  } = useReceiverStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [trackingIdentifierSearch, setTrackingIdentifierSearch] = useState('');
  const [selectedStatus, setSelectedStatus] =
    useState<SettlementStatusFilter>('all');
  const [selectedLogisticsStatus, setSelectedLogisticsStatus] =
    useState<LogisticsStatusFilter>('all');
  const [selectedApiTracking, setSelectedApiTracking] =
    useState<ApiTrackingFilter>('all');
  const [shipmentSort, setShipmentSort] = useState<ShipmentSortSelection>({
    field: null,
    direction: 'ascending',
  });
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedDivision, setSelectedDivision] = useState<string>('all');
  // Divisions only matter for exporters that have more than one.
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const isMultiDivision = divisions.length > 1;
  useEffect(() => {
    const exporterId = userProfile?.exporter?.id;
    if (!exporterId) return;
    getExporterDivisions(exporterId)
      .then(setDivisions)
      .catch((error) => console.error('Error loading divisions:', error));
  }, [userProfile?.exporter?.id]);
  const [selectedPacking, setSelectedPacking] = useState<string>('all');
  const [selectedDimensions, setSelectedDimensions] = useState<
    Record<string, string>
  >({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 20;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [bulkDeletePacking, setBulkDeletePacking] = useState<string>('');
  const [metric, setMetric] = useState<'kg' | 'ceq'>('kg');
  const [weekDateMode, setWeekDateMode] = useState<'etd' | 'eta'>('etd');
  const [summarySourceMode, setSummarySourceMode] =
    useState<ChartSourceMode>('effective');
  const [varietyMode, setVarietyMode] = useState<'real' | 'labeled'>('real');
  const [caliberOrder, setCaliberOrder] = useState<'volume' | 'ascending'>(
    'volume'
  );
  const [settledReferenceKeys, setSettledReferenceKeys] = useState<Set<string>>(
    () => new Set()
  );
  const {
    isOpen: isBulkDeleteModalOpen,
    onOpen: onBulkDeleteModalOpen,
    onClose: onBulkDeleteModalClose,
  } = useDisclosure();
  const {
    isOpen: isReceiverMappingModalOpen,
    onOpen: onReceiverMappingModalOpen,
    onClose: onReceiverMappingModalClose,
  } = useDisclosure();
  const {
    isOpen: isFormatMappingModalOpen,
    onOpen: onFormatMappingModalOpen,
    onClose: onFormatMappingModalClose,
  } = useDisclosure();

  const shipmentRequestVersion = useRef(0);
  const [pendingReplacements, setPendingReplacements] = useState<
    ShipmentReplacement[]
  >([]);
  const [reviewingReplacements, setReviewingReplacements] = useState(false);
  const [isSourceSyncing, setIsSourceSyncing] = useState(false);

  const fetchData = useCallback(
    async (sourceAction?: 'sync' | 'accept_replacement', caseId?: string) => {
      const requestVersion = ++shipmentRequestVersion.current;
      const isCurrentRequest = () =>
        requestVersion === shipmentRequestVersion.current;
      // Use the freshest token for the request without making token changes a
      // data-fetch dependency.
      const { session: latestSession, userProfile: latestUserProfile } =
        useUserStore.getState();
      const exporterId =
        latestUserProfile?.exporter?.id ?? latestUserProfile?.exporter_id;
      if (
        !authenticatedUserId ||
        !userProfileContextKey ||
        !exporterId ||
        !latestSession ||
        !latestUserProfile ||
        !selectedSeason ||
        !selectedSpecies?.id
      ) {
        return;
      }

      setLoading(true);
      setError(null);
      if (!sourceAction) setShipments([]);
      setSettledReferenceKeys(new Set());
      setIsSourceSyncing(Boolean(sourceAction));
      setPendingReplacements([]);
      try {
        if (
          sourceAction &&
          exporterUsesFrutic(latestUserProfile.exporter, selectedSeason)
        ) {
          const replacements = await synchronizeFruticShipments(
            exporterId,
            selectedSeason,
            selectedSpecies.id,
            sourceAction,
            caseId
          );
          if (!isCurrentRequest()) return;
          setPendingReplacements(replacements);
        }
        // Persisted formats are already included in the overview. Discovering
        // unmapped codes for the mapping modal must not delay the table.
        void fetchMappings(
          selectedSpecies.id,
          selectedSeason,
          latestSession,
          '',
          latestUserProfile
        );
        const scope = JSON.stringify([
          latestSession.user?.id,
          exporterId,
          selectedSeason,
          selectedSpecies.id,
        ]);
        const loadPage = async () => {
          const [payload, replacements] = await Promise.all([
            getShipmentsDetailsPage(
              exporterId,
              selectedSeason,
              selectedSpecies.id
            ),
            sourceAction
              ? Promise.resolve(null)
              : getShipmentReconciliationCases(
                  exporterId,
                  selectedSeason,
                  selectedSpecies.id
                ),
          ]);
          if (!payload)
            throw new Error('No shipment overview payload returned');
          return {
            replacements,
            settledReferences: new Set(
              payload.rows
                .filter((row) => row.status === 'settled')
                .map((row) => normalizeShipmentReference(row.reference_number))
            ),
            rows: payload.rows.map((box) => ({
              ...box,
              status: box.status || 'pending',
              estimated_settlement_date: getEstimatedSettlementDate(
                box.ship_eta
              ),
            })) as unknown as ShipmentRecord[],
          };
        };
        // A source refresh must read after its mutation, rather than reusing an
        // ordinary page request that may have started before the sync.
        const result = await (sourceAction
          ? loadPage()
          : runShipmentPageRequest(scope, loadPage));
        if (!isCurrentRequest()) return;
        if (result.replacements) setPendingReplacements(result.replacements);
        setSettledReferenceKeys(result.settledReferences);
        setShipments(result.rows);
      } catch (error) {
        if (!isCurrentRequest()) return;
        console.error('Error fetching shipments:', error);
        setError('Error fetching shipments');
        toast.error(
          error instanceof Error
            ? error.message
            : 'No se pudieron cargar los despachos'
        );
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
          setIsSourceSyncing(false);
        }
      }
    },
    [
      authenticatedUserId,
      fetchMappings,
      selectedSeason,
      selectedSpecies?.id,
      setError,
      setLoading,
      setShipments,
      userProfileContextKey,
      viewMode,
    ]
  );

  useEffect(() => {
    fetchData();
    return () => {
      shipmentRequestVersion.current += 1;
    };
  }, [fetchData]);

  const refreshReceiverMappings = useCallback(() => {
    const { session: latestSession, userProfile: latestUserProfile } =
      useUserStore.getState();
    if (
      !authenticatedUserId ||
      !userProfileContextKey ||
      !selectedSpecies?.id ||
      !selectedSeason ||
      !latestSession ||
      !latestUserProfile
    ) {
      return Promise.resolve();
    }

    return fetchReceiverMappings(
      selectedSpecies.id,
      selectedSeason,
      latestSession,
      latestUserProfile
    );
  }, [
    authenticatedUserId,
    fetchReceiverMappings,
    selectedSeason,
    selectedSpecies?.id,
    userProfileContextKey,
  ]);

  useEffect(() => {
    const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;
    if (!exporterId) return;

    fetchExporterReceivers(exporterId).catch(() => null);
  }, [
    fetchExporterReceivers,
    userProfile?.exporter?.id,
    userProfile?.exporter_id,
  ]);

  useEffect(() => {
    refreshReceiverMappings().catch(() => null);
  }, [refreshReceiverMappings]);

  const handleCloseReceiverMappingModal = useCallback(() => {
    onReceiverMappingModalClose();
    refreshReceiverMappings().catch(() => null);
    fetchData();
  }, [fetchData, onReceiverMappingModalClose, refreshReceiverMappings]);

  const receiverNameById = useMemo(
    () =>
      new Map(
        exporterReceivers.map((receiver) => [
          Number(receiver.id),
          receiver.name,
        ])
      ),
    [exporterReceivers]
  );

  const getDisplayReceiverName = useCallback(
    (shipment: Pick<ShipmentBox, 'exporter_receiver_id' | 'receiver'>) => {
      const exporterReceiverId = Number(shipment.exporter_receiver_id);
      if (exporterReceiverId && receiverNameById.has(exporterReceiverId)) {
        return receiverNameById.get(exporterReceiverId);
      }

      return shipment.receiver || undefined;
    },
    [receiverNameById]
  );

  const formatsByCode = useMemo(() => {
    const formats = new Map<string, string>();
    for (const mapping of mappedFormats) {
      const code = normalizeBoxValue(mapping.frutic_code);
      if (!formats.has(code)) formats.set(code, mapping.cherry_box_format);
    }
    return formats;
  }, [mappedFormats]);
  const getEffectiveBoxFormat = useCallback(
    (box: ShipmentBox) =>
      formatsByCode.get(normalizeBoxValue(box.box_type)) || box.box_format,
    [formatsByCode]
  );

  const getFormatLabel = useCallback(
    (box: ShipmentBox) => {
      const format = getEffectiveBoxFormat(box)?.trim();
      return format && normalizeBoxValue(format) !== 'NA'
        ? `${format} · ${box.box_weight} kg`
        : null;
    },
    [getEffectiveBoxFormat]
  );

  const dimensionFilters = useMemo<ShipmentDimensionFilter[]>(
    () => [
      {
        id: 'variety',
        title: t('Variedad Real'),
        getValue: (box) => box.variety || box.labeled_variety,
      },
      {
        id: 'labeledVariety',
        title: t('Variedad Etiquetada'),
        getValue: (box) => box.labeled_variety || box.variety,
      },
      { id: 'caliber', title: t('Calibre'), getValue: (box) => box.caliber },
      {
        id: 'category',
        title: selectedSpecies?.id === 2 ? t('Color') : t('Categoría'),
        getValue: (box) =>
          selectedSpecies?.id === 2 ? box.color || box.category : box.category,
      },
      { id: 'label', title: t('Etiqueta'), getValue: (box) => box.box_label },
      { id: 'format', title: t('Formato'), getValue: getFormatLabel },
      { id: 'region', title: t('Región'), getValue: (box) => box.sale_region },
      {
        id: 'country',
        title: t('País'),
        getValue: (box) => {
          const tracking = box.tracking_journeys?.[0];
          const code =
            summarySourceMode === 'effective'
              ? trackingCountryCode({
                  countryCode: tracking?.destination_country_code,
                  locode: tracking?.destination_locode,
                })
              : null;
          return code
            ? getCountryDisplayName(code, currentLanguage)
            : box.ship_destination_country;
        },
      },
      {
        id: 'port',
        title: t('Puerto'),
        getValue: (box) =>
          (summarySourceMode === 'effective'
            ? box.tracking_journeys?.[0]?.destination_name?.trim() ||
              normalizeUnLocode(
                box.tracking_journeys?.[0]?.destination_locode
              )?.slice(2)
            : null) || box.ship_destination_port,
      },
      {
        id: 'shipType',
        title: t('Tipo Nave'),
        getValue: (box) => {
          const type =
            box.ship_type ||
            (summarySourceMode === 'effective'
              ? box.tracking_journeys?.[0]?.mode === 'ocean'
                ? 'M'
                : box.tracking_journeys?.[0]?.mode === 'air'
                  ? 'A'
                  : null
              : null);
          return type
            ? { A: t('Aéreo'), M: t('Marítimo'), T: t('Terrestre') }[type] ||
                type
            : null;
        },
      },
      {
        id: 'carrier',
        title: t('Naviera'),
        getValue: (box) =>
          summarySourceMode === 'effective'
            ? box.tracking_journeys?.find((journey) =>
                journey.carrier_name?.trim()
              )?.carrier_name
            : null,
      },
      { id: 'vessel', title: t('Nave'), getValue: (box) => box.ship_name },
    ],
    [t, selectedSpecies?.id, getFormatLabel, summarySourceMode, currentLanguage]
  );

  const dimensionOptions = useMemo(
    () =>
      dimensionFilters.map((dimension) => ({
        ...dimension,
        options: Array.from(
          new Set(shipments.map((box) => dimension.getValue(box)?.trim() || ''))
        )
          .sort((left, right) =>
            dimension.id === 'caliber'
              ? compareShipmentCalibersAscending(
                  left,
                  right,
                  selectedSpecies?.id
                )
              : left.localeCompare(right, currentLanguage, { numeric: true })
          )
          .map((value) => ({
            key: shipmentDimensionKey(value),
            label: value || t('Sin información'),
          })),
      })),
    [dimensionFilters, shipments, selectedSpecies?.id, currentLanguage, t]
  );

  useEffect(() => {
    setSelectedDimensions({});
  }, [
    userProfileContextKey,
    selectedSeason,
    selectedSpecies?.id,
    summarySourceMode,
    currentLanguage,
  ]);

  const dimensionFilteredBoxes = useMemo(
    () =>
      filterShipmentDimensions(
        shipments,
        dimensionFilters,
        selectedDimensions
      ).filter(
        (box) =>
          (selectedClient === 'all' ||
            getDisplayReceiverName(box) === selectedClient) &&
          (selectedPacking === 'all' || box.packing_name === selectedPacking) &&
          (selectedDivision === 'all' ||
            String(box.division_id ?? '') === selectedDivision)
      ),
    [
      shipments,
      dimensionFilters,
      selectedDimensions,
      selectedClient,
      selectedPacking,
      selectedDivision,
      getDisplayReceiverName,
    ]
  );

  const groupedShipments = useMemo(() => {
    const groups = dimensionFilteredBoxes.reduce(
      (acc, shipment) => {
        // One row per shipment: divisions may share a reference.
        const key = String(shipment.shipment_id);
        const reference = normalizeShipmentReference(shipment.reference_number);
        const status =
          shipment.status ||
          (settledReferenceKeys.has(reference) ? 'settled' : 'pending');
        if (!acc[key]) {
          acc[key] = {
            id: key,
            shipment_id: Number(shipment.shipment_id),
            reference_number: reference,
            division_id: shipment.division_id ?? null,
            division_name: shipment.division_name ?? null,
            receiver: getDisplayReceiverName(shipment),
            receiver_names: [],
            packing_names: [],
            has_unmapped_receiver: false,
            exporter_receiver_id: shipment.exporter_receiver_id ?? null,
            business_type: shipment.business_type || null,
            sale_order: shipment.sale_order || null,
            original_incoterm: shipment.original_incoterm || null,
            ship_name: shipment.ship_name,
            ship_etd: shipment.ship_etd,
            ship_eta: shipment.ship_eta,
            ship_destination_country: shipment.ship_destination_country || null,
            ship_destination_port: shipment.ship_destination_port || null,
            estimated_settlement_date: shipment.estimated_settlement_date,
            status,
            total_quantity: 0,
            total_kilos: 0,
            packing_name: shipment.packing_name,
            container_numbers: [],
            awb_numbers: [],
            tracking_journeys: [],
          };
        }
        if (!acc[key].business_type && shipment.business_type) {
          acc[key].business_type = shipment.business_type;
        }
        if (!acc[key].sale_order && shipment.sale_order) {
          acc[key].sale_order = shipment.sale_order;
        }
        if (!acc[key].original_incoterm && shipment.original_incoterm) {
          acc[key].original_incoterm = shipment.original_incoterm;
        }
        acc[key].total_quantity += shipment.quantity || 0;
        const receiverName = getDisplayReceiverName(shipment);
        if (receiverName && !acc[key].receiver_names!.includes(receiverName)) {
          acc[key].receiver_names!.push(receiverName);
        }
        if (
          shipment.packing_name &&
          !acc[key].packing_names!.includes(shipment.packing_name)
        ) {
          acc[key].packing_names!.push(shipment.packing_name);
        }
        if (!shipment.exporter_receiver_id)
          acc[key].has_unmapped_receiver = true;
        acc[key].total_kilos +=
          (shipment.quantity || 0) * (shipment.box_weight || 0);
        // Track unique container numbers per reference
        const cn = (shipment as any).container_number as
          | string
          | null
          | undefined;
        addUniqueShipmentIdentifier(acc[key].container_numbers!, cn);
        addUniqueShipmentIdentifier(acc[key].awb_numbers!, shipment.awb_number);
        for (const journey of shipment.tracking_journeys || []) {
          const identifiers =
            journey.identifier_type === 'container'
              ? acc[key].container_numbers!
              : acc[key].awb_numbers!;
          addUniqueShipmentIdentifier(identifiers, journey.identifier_value);
          if (
            !acc[key].tracking_journeys!.some(
              (existing) => existing.id === journey.id
            )
          ) {
            acc[key].tracking_journeys!.push(journey);
          }
        }
        return acc;
      },
      {} as Record<string, GroupedShipment>
    );

    return Object.values(groups);
  }, [getDisplayReceiverName, dimensionFilteredBoxes, settledReferenceKeys]);

  // Get unique clients for the filter
  const uniqueClients = useMemo(() => {
    const clients = Array.from(
      new Set(shipments.map(getDisplayReceiverName).filter(Boolean))
    ) as string[];
    return clients.sort();
  }, [shipments, getDisplayReceiverName]);

  const uniquePackings = useMemo(() => {
    const clients = Array.from(
      new Set(shipments.map((s) => s.packing_name).filter(Boolean))
    ) as string[];
    return clients.sort();
  }, [shipments]);

  // Base filtered shipments (for status counts) - filtered by client and search but not status
  const baseFilteredShipments = useMemo(() => {
    const normalizedTrackingSearch = normalizeShipmentIdentifier(
      trackingIdentifierSearch
    );

    return groupedShipments.filter((group) => {
      const matchesSearch = group.reference_number
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesTrackingIdentifier =
        normalizedTrackingSearch.length === 0 ||
        [...(group.container_numbers || []), ...(group.awb_numbers || [])].some(
          (identifier) =>
            normalizeShipmentIdentifier(identifier).includes(
              normalizedTrackingSearch
            )
        );
      const matchesClient =
        selectedClient === 'all' ||
        group.receiver_names?.includes(selectedClient);
      const matchesPacking =
        selectedPacking === 'all' ||
        group.packing_names?.includes(selectedPacking);
      return (
        matchesSearch &&
        matchesTrackingIdentifier &&
        matchesClient &&
        matchesPacking
      );
    });
  }, [
    groupedShipments,
    searchTerm,
    selectedClient,
    selectedPacking,
    trackingIdentifierSearch,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    searchTerm,
    trackingIdentifierSearch,
    selectedClient,
    selectedDivision,
    selectedPacking,
    selectedStatus,
    selectedLogisticsStatus,
    selectedApiTracking,
    selectedDimensions,
  ]);

  const filteredShipments = useMemo(() => {
    const today = new Date();

    return baseFilteredShipments.filter((group) => {
      const matchesSettlement =
        selectedStatus === 'all' ||
        getShipmentSettlementStatus(group) === selectedStatus;
      const matchesLogistics =
        selectedLogisticsStatus === 'all' ||
        getShipmentLogisticsStatus(group, today) === selectedLogisticsStatus;
      const hasApiTracking = Boolean(group.tracking_journeys?.length);
      const matchesApiTracking =
        selectedApiTracking === 'all' ||
        (selectedApiTracking === 'tracked' ? hasApiTracking : !hasApiTracking);

      return matchesSettlement && matchesLogistics && matchesApiTracking;
    });
  }, [
    baseFilteredShipments,
    selectedApiTracking,
    selectedLogisticsStatus,
    selectedStatus,
  ]);

  // Box-level data filtered by the same sidebar filters as the table
  const filteredBoxes = useMemo(() => {
    const allowedShipments = new Set(
      filteredShipments.map((g) => g.shipment_id)
    );
    return dimensionFilteredBoxes.filter((b) =>
      allowedShipments.has(Number(b.shipment_id))
    );
  }, [dimensionFilteredBoxes, filteredShipments]);

  const getBoxDisplayReceiverName = useCallback(
    (box: ShipmentBox) => getDisplayReceiverName(box) || t('Sin cliente'),
    [getDisplayReceiverName, t]
  );

  // Summary datasets (Resumen)
  const unitLabel = metric === 'kg' ? 'kg' : 'CEQ';
  const shipmentSummaryMetrics = useMemo(() => {
    const boxSize = selectedSpecies?.default_unit_price || 5;
    const emptyMetric = () => ({ kg: 0, ceq: 0, references: 0 });
    const metrics = {
      total: emptyMetric(),
      logistics: {
        pendingDeparture: emptyMetric(),
        inTransit: emptyMetric(),
        arrived: emptyMetric(),
        missingEta: emptyMetric(),
      },
      settlement: {
        pending: emptyMetric(),
        overdue: emptyMetric(),
        settled: emptyMetric(),
      },
    };
    const today = new Date();

    filteredShipments.forEach((group) => {
      const settlementStatus = getShipmentSettlementStatus(group);
      const logisticsStatus = getShipmentLogisticsStatus(group, today);

      metrics.total.references += 1;
      metrics.settlement[settlementStatus].references += 1;
      metrics.logistics[logisticsStatus].references += 1;
      const kg = group.total_kilos;
      const ceq = kg / boxSize;
      metrics.total.kg += kg;
      metrics.total.ceq += ceq;
      metrics.settlement[settlementStatus].kg += kg;
      metrics.settlement[settlementStatus].ceq += ceq;
      metrics.logistics[logisticsStatus].kg += kg;
      metrics.logistics[logisticsStatus].ceq += ceq;
    });

    return metrics;
  }, [filteredShipments, selectedSpecies?.default_unit_price]);

  const getMetricPercentage = (kg: number) =>
    shipmentSummaryMetrics.total.kg > 0
      ? (kg / shipmentSummaryMetrics.total.kg) * 100
      : 0;

  const formatMetricPercentage = (percentage: number) => {
    if (percentage <= 0) return '0';
    if (percentage >= 99.95) return '100';
    return percentage.toFixed(1);
  };

  const formatChartTooltip = useCallback(
    (value: number, total: number) => {
      const percentage = total > 0 ? (value / total) * 100 : 0;
      return `${formatNumber(value)} ${unitLabel} (${formatMetricPercentage(
        percentage
      )}%)`;
    },
    [unitLabel]
  );

  const renderSourceCoverageChip = (
    coverage: ChartSourceCoverage,
    chipLabel: string,
    apiSourceDescription: string
  ) => {
    const apiReferencePercentage =
      coverage.totalReferences > 0
        ? (coverage.apiSourcedReferences / coverage.totalReferences) * 100
        : 0;
    const apiValuePercentage =
      coverage.totalValue > 0
        ? (coverage.apiSourcedValue / coverage.totalValue) * 100
        : 0;

    return (
      <Tooltip
        placement='top'
        content={
          <div className='max-w-xs space-y-1 px-1 py-0.5 text-xs'>
            <p className='text-default-500'>
              {t(
                'Mide las referencias cuyo dato en este gráfico proviene de la API, no cuántas tienen seguimiento.'
              )}
            </p>
            <p>
              {coverage.apiSourcedReferences}/{coverage.totalReferences}{' '}
              {apiSourceDescription}
            </p>
            <p>
              {formatMetricPercentage(apiValuePercentage)}% {unitLabel}{' '}
              {t('usa datos API')}
            </p>
            <p>
              {coverage.canonicalSourcedReferences}/{coverage.totalReferences}{' '}
              {t('usan Base Despachos')}
            </p>
            {coverage.missingReferences > 0 ? (
              <p>
                {coverage.missingReferences}/{coverage.totalReferences}{' '}
                {t('sin dato disponible')}
              </p>
            ) : null}
          </div>
        }
      >
        <Chip
          size='sm'
          variant='flat'
          color={coverage.apiSourcedReferences > 0 ? 'success' : 'default'}
          className='cursor-help'
        >
          {chipLabel} · {formatMetricPercentage(apiReferencePercentage)}%
        </Chip>
      </Tooltip>
    );
  };

  const logisticsCards = [
    {
      key: 'pendingDeparture',
      title: t('Por despachar'),
      metric: shipmentSummaryMetrics.logistics.pendingDeparture,
      dotClassName: 'bg-default-400',
      progressClassName: 'bg-default-400',
      progressValue: getMetricPercentage(
        shipmentSummaryMetrics.logistics.pendingDeparture.kg
      ),
    },
    {
      key: 'inTransit',
      title: t('En tránsito'),
      metric: shipmentSummaryMetrics.logistics.inTransit,
      dotClassName: 'bg-blue-500',
      progressClassName: 'bg-blue-500',
      progressValue: getMetricPercentage(
        shipmentSummaryMetrics.logistics.inTransit.kg
      ),
    },
    {
      key: 'arrived',
      title: t('Arribado'),
      metric: shipmentSummaryMetrics.logistics.arrived,
      dotClassName: 'bg-green-500',
      progressClassName: 'bg-green-500',
      progressValue: getMetricPercentage(
        shipmentSummaryMetrics.logistics.arrived.kg
      ),
    },
  ];

  const settlementCards = [
    {
      key: 'pending',
      title: t('Pendiente'),
      metric: shipmentSummaryMetrics.settlement.pending,
      dotClassName: 'bg-amber-400',
      progressClassName: 'bg-amber-400',
      progressValue: getMetricPercentage(
        shipmentSummaryMetrics.settlement.pending.kg
      ),
    },
    {
      key: 'overdue',
      title: t('Vencida'),
      metric: shipmentSummaryMetrics.settlement.overdue,
      dotClassName: 'bg-pink-600',
      progressClassName: 'bg-pink-600',
      progressValue: getMetricPercentage(
        shipmentSummaryMetrics.settlement.overdue.kg
      ),
    },
    {
      key: 'settled',
      title: t('Liquidado'),
      metric: shipmentSummaryMetrics.settlement.settled,
      dotClassName: 'bg-green-500',
      progressClassName: 'bg-green-500',
      progressValue: getMetricPercentage(
        shipmentSummaryMetrics.settlement.settled.kg
      ),
    },
  ];

  const summaryLocale = currentLanguage.startsWith('en') ? 'en-US' : 'es-CL';
  const formatSummaryNumber = (value: number, decimals = 1) =>
    value.toLocaleString(summaryLocale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  const formatSummaryVolume = (value: number) => {
    if (Math.abs(value) >= 1_000_000) {
      return `${(value / 1_000_000).toLocaleString(summaryLocale, {
        maximumFractionDigits: 2,
      })}M`;
    }
    if (Math.abs(value) >= 1_000) {
      return `${formatSummaryNumber(value / 1_000, 0)}k`;
    }
    return value.toLocaleString(summaryLocale, { maximumFractionDigits: 1 });
  };

  const weekVolumeSeries = useMemo(() => {
    const byWeek = new Map<string, number>();
    const getCanonicalDate = (shipment: GroupedShipment) =>
      weekDateMode === 'etd' ? shipment.ship_etd : shipment.ship_eta;
    const getLiveDate = (shipment: GroupedShipment) => {
      const tracking = shipment.tracking_journeys?.[0];
      return weekDateMode === 'etd' ? tracking?.live_etd : tracking?.live_eta;
    };
    const getChartDate = (shipment: GroupedShipment) =>
      summarySourceMode === 'effective'
        ? getLiveDate(shipment) || getCanonicalDate(shipment)
        : getCanonicalDate(shipment);
    const getDateSource = (shipment: GroupedShipment): ChartDataSource => {
      if (summarySourceMode === 'effective' && getLiveDate(shipment)) {
        return 'api';
      }
      return getCanonicalDate(shipment) ? 'canonical' : null;
    };
    const getGroupValue = (shipment: GroupedShipment) =>
      metric === 'kg'
        ? shipment.total_kilos
        : shipment.total_kilos / (selectedSpecies?.default_unit_price || 5);

    for (const shipment of filteredShipments) {
      const dateValue = getChartDate(shipment);
      if (!dateValue) continue;
      const w = getWeekKey(dateValue) || t('Sin semana');
      byWeek.set(w, (byWeek.get(w) || 0) + getGroupValue(shipment));
    }
    const entries = Array.from(byWeek.entries()).sort((a, b) =>
      sortWeekKeys(a[0], b[0])
    );
    const categories = entries.map((e) => e[0]);
    const data = entries.map((e) => Number(e[1].toFixed(2)));
    const displayedTotal = data.reduce((sum, value) => sum + value, 0);
    const options: ApexOptions & {
      plotOptions: { bar: { borderRadius: number; columnWidth: string } };
    } = {
      chart: {
        type: 'bar',
        background: 'transparent',
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        toolbar: { show: false },
      },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { formatter: (value: string) => value?.replace(/^\d{4}-/, '') },
      },
      yaxis: { show: false },
      grid: { show: false },
      theme: { mode: darkMode ? 'dark' : 'light' },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '75%' } },
      dataLabels: { enabled: false },
      tooltip: {
        x: {
          formatter: (_value, context) => categories[context.dataPointIndex],
        },
        y: {
          formatter: (v) => formatChartTooltip(Number(v), displayedTotal),
        },
      },
      fill: { opacity: 1 },
      colors: ['#2f71e8'],
      noData: { text: t('No se encontraron despachos') },
    };
    const coverage = getChartSourceCoverage(
      filteredShipments,
      getGroupValue,
      getDateSource
    );
    const missingDateReferences = filteredShipments.reduce(
      (total, shipment) => total + (getChartDate(shipment) ? 0 : 1),
      0
    );
    return {
      options,
      series: [{ name: unitLabel, data }],
      coverage,
      missingDateReferences,
    };
  }, [
    filteredShipments,
    formatChartTooltip,
    metric,
    selectedSpecies?.default_unit_price,
    unitLabel,
    weekDateMode,
    summarySourceMode,
    darkMode,
    t,
  ]);

  const destinationCountrySeries = useMemo(() => {
    const byCountry = new Map<string, number>();
    const getTrackedCountry = (shipment: GroupedShipment) => {
      const tracking = shipment.tracking_journeys?.[0];
      const countryCode = trackingCountryCode({
        countryCode: tracking?.destination_country_code,
        locode: tracking?.destination_locode,
      });
      return countryCode
        ? getCountryDisplayName(countryCode, currentLanguage)
        : null;
    };
    const getCountry = (shipment: GroupedShipment) =>
      (summarySourceMode === 'effective'
        ? getTrackedCountry(shipment) ||
          shipment.ship_destination_country?.trim()
        : shipment.ship_destination_country?.trim()) || t('Sin país');
    const getCountrySource = (shipment: GroupedShipment): ChartDataSource => {
      if (summarySourceMode === 'effective' && getTrackedCountry(shipment)) {
        return 'api';
      }
      if (shipment.ship_destination_country?.trim()) return 'canonical';
      return 'missing';
    };
    const getGroupValue = (shipment: GroupedShipment) =>
      metric === 'kg'
        ? shipment.total_kilos
        : shipment.total_kilos / (selectedSpecies?.default_unit_price || 5);

    for (const shipment of filteredShipments) {
      const country = getCountry(shipment);
      byCountry.set(
        country,
        (byCountry.get(country) || 0) + getGroupValue(shipment)
      );
    }
    const entries = Array.from(byCountry.entries()).sort((a, b) => b[1] - a[1]);
    const coverage = getChartSourceCoverage(
      filteredShipments,
      getGroupValue,
      getCountrySource
    );
    return {
      entries: entries.map(([label, value]) => ({ label, value })),
      coverage,
    };
  }, [
    filteredShipments,
    metric,
    currentLanguage,
    selectedSpecies?.default_unit_price,
    summarySourceMode,
    t,
  ]);

  const destinationPortSeries = useMemo(() => {
    const byPort = new Map<string, number>();
    const getTrackedPort = (shipment: GroupedShipment) => {
      const tracking = shipment.tracking_journeys?.[0];
      return (
        tracking?.destination_name?.trim() ||
        normalizeUnLocode(tracking?.destination_locode)?.slice(2) ||
        null
      );
    };
    const getPort = (shipment: GroupedShipment) =>
      (summarySourceMode === 'effective'
        ? getTrackedPort(shipment) || shipment.ship_destination_port?.trim()
        : shipment.ship_destination_port?.trim()) || t('Sin puerto');
    const getPortSource = (shipment: GroupedShipment): ChartDataSource => {
      if (summarySourceMode === 'effective' && getTrackedPort(shipment)) {
        return 'api';
      }
      if (shipment.ship_destination_port?.trim()) return 'canonical';
      return 'missing';
    };
    const getGroupValue = (shipment: GroupedShipment) =>
      metric === 'kg'
        ? shipment.total_kilos
        : shipment.total_kilos / (selectedSpecies?.default_unit_price || 5);

    for (const shipment of filteredShipments) {
      const port = getPort(shipment);
      byPort.set(port, (byPort.get(port) || 0) + getGroupValue(shipment));
    }
    const entries = Array.from(byPort.entries()).sort((a, b) => b[1] - a[1]);
    const coverage = getChartSourceCoverage(
      filteredShipments,
      getGroupValue,
      getPortSource
    );
    return {
      entries: entries.map(([label, value]) => ({ label, value })),
      coverage,
    };
  }, [
    filteredShipments,
    metric,
    selectedSpecies?.default_unit_price,
    summarySourceMode,
    t,
  ]);

  const boxDistribution = useCallback(
    (getLabel: (box: ShipmentBox) => string | null | undefined) =>
      viewMode === 'resumen'
        ? getShipmentDistribution(
            filteredBoxes,
            getLabel,
            metric,
            selectedSpecies?.default_unit_price || 5,
            t('Sin información')
          )
        : [],
    [viewMode, filteredBoxes, metric, selectedSpecies?.default_unit_price, t]
  );
  const formatDistribution = useMemo(
    () => boxDistribution(getFormatLabel),
    [boxDistribution, getFormatLabel]
  );
  const hasMissingFormats = filteredBoxes.some((box) => {
    const format = normalizeBoxValue(getEffectiveBoxFormat(box));
    return !format || format === 'NA' || format === 'NO ENCONTRADO';
  });
  const distributionSections = useMemo(
    () => [
      {
        id: 'fruit',
        title: t('De la fruta'),
        color: '#9456cf',
        rows: [
          {
            id: 'variety',
            title: t('Variedad'),
            entries: boxDistribution((box) =>
              varietyMode === 'labeled'
                ? box.labeled_variety || box.variety
                : box.variety || box.labeled_variety
            ),
            rowControls: (
              <SegmentedToggle
                compact
                value={varietyMode}
                options={[
                  { value: 'real', label: t('Real') },
                  {
                    value: 'labeled',
                    label: t('Etiquetada'),
                    shortLabel: t('Etiq.'),
                  },
                ]}
                onChange={setVarietyMode}
              />
            ),
          },
          {
            id: 'caliber',
            title: t('Calibre'),
            entries:
              caliberOrder === 'ascending'
                ? boxDistribution((box) => box.caliber).sort((left, right) =>
                    compareShipmentCalibersAscending(
                      left.label,
                      right.label,
                      selectedSpecies?.id
                    )
                  )
                : boxDistribution((box) => box.caliber),
            rowControls: (
              <SegmentedToggle
                compact
                value={caliberOrder}
                options={[
                  { value: 'volume', label: t('Volumen') },
                  {
                    value: 'ascending',
                    label: t('Ascendente'),
                    shortLabel: t('Calibre'),
                  },
                ]}
                onChange={setCaliberOrder}
              />
            ),
          },
          {
            id: 'category',
            title: selectedSpecies?.id === 2 ? t('Color') : t('Categoría'),
            entries: boxDistribution((box) =>
              selectedSpecies?.id === 2
                ? box.color || box.category
                : box.category
            ),
          },
        ],
      },
      {
        id: 'packaging',
        title: t('Del embalaje'),
        color: '#58c66b',
        rows: [
          {
            id: 'packing',
            title: t('Packing'),
            entries: boxDistribution((box) => box.packing_name),
          },
          {
            id: 'label',
            title: t('Etiqueta'),
            entries: boxDistribution((box) => box.box_label),
          },
          {
            id: 'format',
            title: t('Formato'),
            entries: formatDistribution,
            rowControls: hasMissingFormats ? (
              <MapFormatsButton
                onPress={onFormatMappingModalOpen}
                label={t('Mapear formatos')}
                tooltip={t('Abrir mapeo de formatos')}
              />
            ) : undefined,
          },
        ],
      },
      {
        id: 'markets',
        title: t('De los mercados'),
        color: '#eead3f',
        rows: [
          {
            id: 'region',
            title: t('Región'),
            entries: boxDistribution((box) => box.sale_region),
          },
          {
            id: 'country',
            title: t('País'),
            entries: destinationCountrySeries.entries,
            controls: renderSourceCoverageChip(
              destinationCountrySeries.coverage,
              t('País desde API'),
              t('usan destino final de trackeo')
            ),
          },
          {
            id: 'port',
            title: t('Puerto'),
            entries: destinationPortSeries.entries,
            controls: renderSourceCoverageChip(
              destinationPortSeries.coverage,
              t('Puerto desde API'),
              t('usan destino final de trackeo')
            ),
          },
        ],
      },
      {
        id: 'logistics',
        title: t('De la logística'),
        color: '#2f71e8',
        rows: [
          {
            id: 'shipType',
            title: t('Tipo Nave'),
            entries: boxDistribution((box) => {
              const type =
                box.ship_type ||
                (summarySourceMode === 'effective'
                  ? box.tracking_journeys?.[0]?.mode === 'ocean'
                    ? 'M'
                    : box.tracking_journeys?.[0]?.mode === 'air'
                      ? 'A'
                      : null
                  : null);
              return type
                ? { A: t('Aéreo'), M: t('Marítimo'), T: t('Terrestre') }[
                    type
                  ] || type
                : null;
            }),
          },
          {
            id: 'carrier',
            title: t('Naviera'),
            description: t('Naviera informada por seguimiento'),
            entries: boxDistribution((box) =>
              summarySourceMode === 'effective'
                ? box.tracking_journeys?.find((journey) =>
                    journey.carrier_name?.trim()
                  )?.carrier_name
                : null
            ),
          },
          {
            id: 'vessel',
            title: t('Nave'),
            entries: boxDistribution((box) => box.ship_name),
          },
        ],
      },
    ],
    [
      boxDistribution,
      varietyMode,
      caliberOrder,
      selectedSpecies?.id,
      t,
      formatDistribution,
      hasMissingFormats,
      onFormatMappingModalOpen,
      destinationCountrySeries,
      destinationPortSeries,
      summarySourceMode,
      unitLabel,
    ]
  );
  const receiverDistribution = useMemo(
    () => boxDistribution(getBoxDisplayReceiverName),
    [boxDistribution, getBoxDisplayReceiverName]
  );

  const shipmentSortGroups = useMemo(
    () => ({
      shipment: [
        { field: 'client', label: t('Cliente') },
        { field: 'reference', label: t('Referencia') },
      ] as ShipmentSortOption[],
      container: [
        { field: 'container', label: t('Contenedor') },
        { field: 'kilos', label: metric === 'kg' ? t('Kg') : 'CEQ' },
      ] as ShipmentSortOption[],
      logistics: [
        { field: 'logisticsStatus', label: t('Estado logística') },
        { field: 'etd', label: t('Fecha ETD') },
        { field: 'eta', label: t('Fecha ETA') },
        { field: 'vessel', label: t('Nave') },
        { field: 'destination', label: t('Puerto destino') },
        { field: 'apiTracking', label: t('Seguimiento API') },
      ] as ShipmentSortOption[],
      settlement: [
        { field: 'settlementStatus', label: t('Estado liquidación') },
        {
          field: 'settlementDate',
          label: t('Fecha estimada liquidación'),
        },
      ] as ShipmentSortOption[],
    }),
    [metric, t]
  );

  const handleShipmentSortSelect = useCallback(
    (field: ShipmentManualSortField | null) => {
      setPage(1);
      setShipmentSort((current) => {
        if (field === null) return { field: null, direction: 'ascending' };
        if (current.field === field) {
          return {
            field,
            direction:
              current.direction === 'ascending' ? 'descending' : 'ascending',
          };
        }
        return { field, direction: shipmentSortDefaultDirections[field] };
      });
    },
    []
  );

  const sortedShipments = useMemo(() => {
    const today = new Date();
    return [...filteredShipments].sort((left, right) =>
      shipmentSort.field
        ? compareShipmentsByField(
            left,
            right,
            shipmentSort.field,
            shipmentSort.direction,
            today
          )
        : compareShipmentsByOperationalPriority(left, right, today)
    );
  }, [filteredShipments, shipmentSort]);

  const pages = Math.ceil(sortedShipments.length / rowsPerPage);

  const paginatedShipments = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedShipments.slice(start, end);
  }, [page, sortedShipments]);

  const handleUploadComplete = () => {
    fetchData();
  };

  const getEstimatedSettlementDate = (eta: string) => {
    if (!eta || eta === '') return '';
    const etaDate = new Date(eta);
    const settlementDate = new Date(etaDate);
    settlementDate.setDate(settlementDate.getDate() + 30);
    return settlementDate;
  };

  const handleHeaderSaved = useCallback(
    (updated: Shipment, journeys: ShipmentTrackingSummary[] | null) => {
      const fields = Object.fromEntries(
        shipmentHeaderFields.map((field) => [
          field.key,
          updated[field.key] ?? '',
        ])
      ) as Partial<ShipmentBox>;
      setShipments((current) =>
        current.map((box) =>
          Number(box.shipment_id) === updated.id
            ? {
                ...box,
                ...fields,
                exporter_receiver_id: updated.exporter_receiver_id ?? null,
                tracking_journeys: journeys ?? box.tracking_journeys,
                estimated_settlement_date: getEstimatedSettlementDate(
                  updated.ship_eta || ''
                ),
              }
            : box
        )
      );
    },
    [setShipments]
  );
  const handleDivisionChanged = useCallback(
    (updated: Shipment) => {
      setShipments((current) =>
        current.map((box) =>
          Number(box.shipment_id) === updated.id
            ? {
                ...box,
                division_id: updated.division_id ?? null,
                division_name:
                  divisions.find((d) => d.id === updated.division_id)?.name ??
                  box.division_name,
              }
            : box
        )
      );
    },
    [setShipments, divisions]
  );
  // A mapping made from the details sheet applies to every shipment with that
  // receiver name in the season, so the list follows without a reload.
  const handleReceiverMapped = useCallback(
    (updated: Shipment) => {
      const name = (updated.receiver || '').trim().toUpperCase();
      setShipments((current) =>
        current.map((box) =>
          (box.receiver || '').trim().toUpperCase() === name
            ? { ...box, exporter_receiver_id: updated.exporter_receiver_id }
            : box
        )
      );
      refreshReceiverMappings().catch(() => null);
    },
    [setShipments, refreshReceiverMappings]
  );
  const handleCreateReceiver = useCallback(
    async (name: string) => {
      const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;
      if (!exporterId)
        throw new Error(t('No se pudo identificar al exportador'));
      const created = await addExporterReceiver(exporterId, name);
      return created.id;
    },
    [
      addExporterReceiver,
      userProfile?.exporter?.id,
      userProfile?.exporter_id,
      t,
    ]
  );
  const handleTrackingUpdated = useCallback(
    (shipmentId: number, journeys: ShipmentTrackingSummary[]) => {
      setShipments((current) =>
        current.map((box) =>
          Number(box.shipment_id) === shipmentId
            ? { ...box, tracking_journeys: journeys }
            : box
        )
      );
    },
    [setShipments]
  );
  const handlePriceFormatMapped = useCallback(
    (mapping: BoxFormatMapping) => {
      setShipments((current) =>
        current.map((box) =>
          Number(box.exporter_id) === mapping.exporter_id &&
          normalizeBoxValue(box.box_type) ===
            normalizeBoxValue(mapping.frutic_code)
            ? { ...box, box_format: mapping.cherry_box_format }
            : box
        )
      );
    },
    [setShipments]
  );
  const details = useShipmentDetails({
    scope:
      authenticatedUserId &&
      userProfileContextKey &&
      selectedSeason &&
      selectedSpecies?.id
        ? JSON.stringify([
            authenticatedUserId,
            userProfileContextKey,
            selectedSeason,
            selectedSpecies.id,
          ])
        : null,
    exporterId: userProfile?.exporter?.id ?? userProfile?.exporter_id,
    season: selectedSeason,
    speciesId: selectedSpecies?.id,
    usesFrutic,
    onHeaderSaved: handleHeaderSaved,
    onDivisionChanged: handleDivisionChanged,
    onReceiverMapped: handleReceiverMapped,
    onTrackingUpdated: handleTrackingUpdated,
    onFormatMapped: handlePriceFormatMapped,
  });
  const { handleViewDetails } = details;

  const handleDeleteShipmentBoxes = useCallback(
    async (shipmentId: number, referenceNumber: string) => {
      if (!userProfile?.exporter?.id) {
        toast.error(t('No se pudo identificar al exportador'));
        return;
      }

      const confirmDelete = window.confirm(
        t(
          '¿Está seguro de eliminar todos los bultos del despacho? Esta acción no se puede deshacer.',
          { reference: referenceNumber }
        )
      );

      if (!confirmDelete) return;

      setIsDeleting(true);
      try {
        await deleteShipment(shipmentId);

        // Filter out the deleted shipment boxes from the current state
        setShipments((prevShipments) =>
          prevShipments.filter((box) => Number(box.shipment_id) !== shipmentId)
        );

        toast.success(
          t('Despacho eliminado correctamente', { reference: referenceNumber })
        );
      } catch (error) {
        console.error('Error deleting shipment boxes:', error);
        toast.error(t('Error al eliminar el despacho'));
      } finally {
        setIsDeleting(false);
      }
    },
    [userProfile?.exporter?.id]
  );

  const handleBulkDeleteByPacking = useCallback(async () => {
    if (!userProfile?.exporter?.id || !bulkDeletePacking) {
      toast.error(
        t('Debe seleccionar un packing específico para eliminar masivamente')
      );
      return;
    }

    setIsBulkDeleting(true);
    try {
      // Count how many shipments will be deleted
      const shipmentsToDelete = shipments.filter(
        (box) => box.packing_name === bulkDeletePacking
      );

      if (shipmentsToDelete.length === 0) {
        toast.info(
          t('No hay despachos para eliminar con el packing seleccionado')
        );
        setIsBulkDeleting(false);
        return;
      }

      // Delete all shipment boxes for the selected packing
      await deleteShipmentsByPacking(
        userProfile.exporter.id,
        selectedSeason,
        selectedSpecies?.id as number,
        bulkDeletePacking
      );

      // Filter out the deleted shipment boxes from the current state
      setShipments((prevShipments) =>
        prevShipments.filter((box) => box.packing_name !== bulkDeletePacking)
      );

      toast.success(
        t('Bultos del packing eliminados correctamente', {
          count: countShipmentBoxRows(shipmentsToDelete),
          packing: bulkDeletePacking,
        })
      );
      onBulkDeleteModalClose();

      // Reset bulk delete packing selection
      setBulkDeletePacking('');
    } catch (error) {
      console.error('Error deleting shipment boxes by packing:', error);
      toast.error(t('Error al eliminar los despachos del packing'));
    } finally {
      setIsBulkDeleting(false);
    }
  }, [
    userProfile?.exporter?.id,
    bulkDeletePacking,
    selectedSeason,
    selectedSpecies?.id,
    shipments,
    onBulkDeleteModalClose,
  ]);

  const renderCell = useCallback(
    (group: GroupedShipment, columnKey: React.Key): React.ReactNode => {
      switch (columnKey) {
        case 'shipment_summary':
          return (
            <div className='relative min-w-64 pr-16'>
              <div className='flex min-w-0 items-center gap-2'>
                <span
                  className='min-w-0 truncate text-sm font-semibold text-foreground'
                  title={group.receiver_names?.join(', ') || t('Sin cliente')}
                >
                  {String(group.receiver || t('Sin cliente'))}
                  {(group.receiver_names?.length || 0) > 1
                    ? ` +${group.receiver_names!.length - 1}`
                    : ''}
                </span>
                {group.has_unmapped_receiver ? (
                  <button
                    type='button'
                    onClick={onReceiverMappingModalOpen}
                    className='shrink-0 rounded-full border border-warning-400/40 bg-warning-400/10 px-2 py-0.5 text-[11px] font-semibold text-warning-600 transition-colors hover:bg-warning-400/20 dark:text-warning-400'
                  >
                    {t('Sin mapear')}
                  </button>
                ) : null}
              </div>
              <span
                className='mt-1 flex min-w-0 items-center gap-2 font-mono text-[13px] text-default-500'
                title={group.reference_number}
              >
                <span className='truncate'>{group.reference_number}</span>
                {isMultiDivision && group.division_name ? (
                  <Chip
                    size='sm'
                    variant='flat'
                    className='h-5 shrink-0 font-sans text-[11px]'
                  >
                    {group.division_name}
                  </Chip>
                ) : null}
              </span>
              <div className='absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-100 transition-opacity lg:pointer-events-none lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100 lg:group-focus-within:pointer-events-auto lg:group-focus-within:opacity-100'>
                <Tooltip
                  placement='top'
                  content={t('Ver detalles del despacho')}
                >
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    className='h-7 min-w-7 text-default-500'
                    aria-label={t('Ver detalles del despacho')}
                    onPress={() =>
                      handleViewDetails(
                        group.reference_number,
                        'general',
                        group.tracking_journeys || [],
                        group.shipment_id
                      )
                    }
                  >
                    <Icon icon='tabler:eye' className='text-sm' />
                  </Button>
                </Tooltip>
                {!usesFrutic ? (
                  <Tooltip placement='top' content={t('Eliminar')}>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='light'
                      color='danger'
                      className='h-7 min-w-7'
                      onPress={() =>
                        handleDeleteShipmentBoxes(
                          group.shipment_id,
                          group.reference_number
                        )
                      }
                      isLoading={isDeleting}
                    >
                      <Icon icon='tabler:trash' className='text-sm' />
                    </Button>
                  </Tooltip>
                ) : null}
              </div>
            </div>
          );
        case 'container_summary': {
          const containers = group.container_numbers || [];
          const containerLabel = containers[0] || '-';
          const extra =
            containers.length > 1 ? ` +${containers.length - 1}` : '';
          const ceqFactor = selectedSpecies?.default_unit_price || 5;
          const displayedVolume =
            metric === 'kg' ? group.total_kilos : group.total_kilos / ceqFactor;
          const displayedUnit = metric === 'kg' ? 'kg' : 'CEQ';

          return (
            <div className='min-w-40'>
              <div
                className='truncate font-mono text-sm font-medium text-foreground'
                title={containers.join(', ')}
              >
                {containerLabel}
                {extra}
              </div>
              <div className='mt-1 text-[13px] text-default-500'>
                {formatNumber(displayedVolume, { decimals: 2 })} {displayedUnit}
              </div>
            </div>
          );
        }
        case 'logistics_summary': {
          const today = new Date();
          const plannedStatus = getPlannedShipmentStatus(
            group.ship_etd,
            group.ship_eta,
            today
          );
          const logisticsStatus = getShipmentLogisticsStatus(group, today);
          const trackingJourneys = group.tracking_journeys || [];
          const primaryTracking = trackingJourneys[0];
          const latestActualEvent = primaryTracking?.latest_actual_event;
          const latestActualEventType =
            latestActualEvent?.event_type.toUpperCase();
          const hasReturnedContainer = Boolean(
            primaryTracking?.observation_status === 'container_returned' ||
              primaryTracking?.provider_container_status === 'EMPTY_RETURN'
          );
          const hasConfirmedTrackingData = Boolean(
            latestActualEvent || hasReturnedContainer
          );
          const statusConfig = {
            missingEta: { label: t('Sin ETA'), color: 'warning' },
            pendingDeparture: {
              label: t('Por despachar · estimado'),
              color: 'default',
            },
            arrived: {
              label: t('Arribado · según ETA'),
              color: 'success',
            },
            inTransit: { label: t('En tránsito · estimado'), color: 'primary' },
            expectedToday: {
              label: t('Llegada prevista hoy'),
              color: 'warning',
            },
            etaOverdue: {
              label: t('ETA vencida · sin confirmar'),
              color: 'warning',
            },
          } as const;
          const trackedEventColor = latestActualEventType
            ? arrivedTrackingEvents.has(latestActualEventType)
              ? 'success'
              : inTransitTrackingEvents.has(latestActualEventType)
                ? 'primary'
                : 'default'
            : 'default';
          const config = latestActualEvent
            ? {
                label:
                  latestActualEvent.description?.trim() ||
                  t(getTrackingEventLabel(latestActualEvent.event_type)),
                color: trackedEventColor,
              }
            : hasReturnedContainer
              ? {
                  label: t('Contenedor vacío devuelto'),
                  color: 'success' as const,
                }
              : statusConfig[
                  trackingJourneys.length > 0 ? plannedStatus : logisticsStatus
                ];
          const effectiveEtd = primaryTracking?.live_etd || group.ship_etd;
          const effectiveEta = primaryTracking?.live_eta || group.ship_eta;
          const vesselName =
            latestActualEvent?.vessel_name || group.ship_name || '-';
          const canonicalDestinationPort =
            group.ship_destination_port?.trim() || null;
          const destinationLocode =
            normalizeUnLocode(primaryTracking?.destination_locode) ||
            normalizeUnLocode(canonicalDestinationPort);
          const destinationCountryCode = trackingCountryCode({
            countryCode: primaryTracking?.destination_country_code,
            locode: destinationLocode,
            fallbackCountry: group.ship_destination_country,
          });
          const destinationFlag = destinationCountryCode
            ? countryCodeToFlag(destinationCountryCode)
            : null;
          // Ports show their LOCODE port part (SHA); airports their IATA (PVG).
          const airportCode = /^[A-Z]{3}$/.test(
            primaryTracking?.destination_locode?.trim().toUpperCase() ?? ''
          )
            ? primaryTracking!.destination_locode!.trim().toUpperCase()
            : null;
          const destinationPortLabel =
            destinationLocode?.slice(2) ||
            airportCode ||
            canonicalDestinationPort?.toUpperCase() ||
            null;
          const etaDayOffset = effectiveEta
            ? getShipmentCalendarDayOffset(effectiveEta, today)
            : null;
          const etaRelativeLabel =
            etaDayOffset === null
              ? null
              : etaDayOffset === 0
                ? t('hoy')
                : etaDayOffset === 1
                  ? t('mañana')
                  : etaDayOffset === -1
                    ? t('ayer')
                    : etaDayOffset > 1
                      ? t('en días', { days: etaDayOffset })
                      : t('hace días', { days: Math.abs(etaDayOffset) });

          return (
            <div className='min-w-[28rem]'>
              <div className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
                <Chip size='sm' color={config.color} variant='flat'>
                  {config.label}
                </Chip>
                <span
                  className='min-w-0 max-w-52 truncate text-sm text-default-600'
                  title={vesselName}
                >
                  {vesselName}
                </span>
                {primaryTracking ? (
                  <button
                    type='button'
                    onClick={() =>
                      handleViewDetails(
                        group.reference_number,
                        'tracking',
                        trackingJourneys,
                        group.shipment_id
                      )
                    }
                    className={
                      hasConfirmedTrackingData
                        ? 'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium text-success-600 transition-colors hover:bg-success-50 hover:text-success-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-success dark:text-success-400 dark:hover:bg-success-500/10'
                        : 'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium text-warning-600 transition-colors hover:bg-warning-50 hover:text-warning-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning dark:text-warning-400 dark:hover:bg-warning-500/10'
                    }
                    aria-label={t('Ver seguimiento del despacho')}
                  >
                    <span
                      className={
                        hasConfirmedTrackingData
                          ? 'h-1.5 w-1.5 rounded-full bg-success'
                          : 'h-1.5 w-1.5 rounded-full bg-warning'
                      }
                    />
                    {hasConfirmedTrackingData
                      ? t('API · Trackeado')
                      : t('API · esperando hitos')}
                  </button>
                ) : (
                  <Tooltip
                    placement='top'
                    content='Este despacho no tiene seguimiento activo'
                  >
                    <span className='inline-flex items-center gap-1 text-[11px] font-medium text-default-400'>
                      <span className='h-1.5 w-1.5 rounded-full bg-default-300' />
                      {t('No trackeado')}
                    </span>
                  </Tooltip>
                )}
              </div>
              <div className='mt-2 flex min-w-0 items-center gap-2 text-[13px] text-default-500'>
                <span>
                  {effectiveEtd ? formatDate(effectiveEtd, 'dd MMM') : '—'}
                </span>
                <span aria-hidden='true' className='text-default-300'>
                  ⟶
                </span>
                <span className='font-semibold text-foreground'>
                  {effectiveEta ? formatDate(effectiveEta, 'dd MMM yyyy') : '—'}
                </span>
                {destinationPortLabel ? (
                  <>
                    <span aria-hidden='true' className='text-default-300'>
                      ·
                    </span>
                    <span
                      className='inline-flex min-w-0 max-w-36 items-center gap-1 font-medium text-default-500'
                      title={
                        primaryTracking?.destination_name ||
                        canonicalDestinationPort ||
                        undefined
                      }
                    >
                      {destinationFlag ? <span>{destinationFlag}</span> : null}
                      <span className='truncate'>{destinationPortLabel}</span>
                    </span>
                  </>
                ) : null}
                {etaRelativeLabel ? (
                  <span
                    className={`rounded-full px-2 py-0.5 font-medium ${
                      etaDayOffset !== null && etaDayOffset < 0
                        ? 'bg-danger-400/15 text-danger-600 dark:text-danger-400'
                        : etaDayOffset !== null &&
                            etaDayOffset > 0 &&
                            etaDayOffset <= 7
                          ? 'bg-warning-400/15 text-warning-600 dark:text-warning-400'
                          : 'bg-default-100 text-default-500'
                    }`}
                  >
                    {etaRelativeLabel}
                  </span>
                ) : null}
              </div>
            </div>
          );
        }
        case 'settlement_summary': {
          const isOverdue =
            group.status === 'pending' &&
            group.estimated_settlement_date &&
            new Date(group.estimated_settlement_date) < new Date();
          const settlementDate = group.estimated_settlement_date
            ? new Date(group.estimated_settlement_date)
            : null;
          const settlementDays = settlementDate
            ? getDaysSinceArrival(settlementDate.toISOString())
            : null;
          const settlementRelativeLabel =
            settlementDays === null
              ? null
              : settlementDays === 0
                ? t('hoy')
                : settlementDays > 0
                  ? t('hace días', { days: settlementDays })
                  : t('en días', { days: Math.abs(settlementDays) });

          return (
            <div className='min-w-56'>
              <Chip
                size='sm'
                color={
                  group.status === 'settled'
                    ? 'success'
                    : isOverdue
                      ? 'danger'
                      : 'warning'
                }
                variant='flat'
              >
                {group.status === 'settled'
                  ? t('Liquidado')
                  : isOverdue
                    ? t('Atrasada')
                    : t('Pendiente')}
              </Chip>
              <div className='mt-2 flex items-center gap-2 text-[13px] text-default-500'>
                <span>
                  {settlementDate
                    ? formatDate(settlementDate.toISOString(), 'dd MMM yyyy')
                    : '—'}
                </span>
                {settlementRelativeLabel ? (
                  <span className='rounded-full bg-default-100 px-2 py-0.5 font-medium text-default-500'>
                    {settlementRelativeLabel}
                  </span>
                ) : null}
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    },
    [
      isDeleting,
      isMultiDivision,
      usesFrutic,
      handleDeleteShipmentBoxes,
      handleViewDetails,
      metric,
      onReceiverMappingModalOpen,
      selectedSpecies?.default_unit_price,
      t,
    ]
  );

  const handleExportShipments = async (withLiquidatedDetails = false) => {
    const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;
    if (!exporterId || !selectedSeason || !selectedSpecies?.id) {
      toast.error(t('No se pudo identificar el exportador'));
      return;
    }

    setIsExporting(true);
    try {
      const mappings = useFruticStore.getState().mappedFormats;
      const boxesForExport =
        (await getShipmentBoxesForExport(
          exporterId,
          selectedSeason,
          selectedSpecies.id
        )) || [];

      const exportShipments = boxesForExport.map((box) => {
        const mapping = mappings.find(
          (m) =>
            normalizeBoxValue(m.frutic_code) === normalizeBoxValue(box.box_type)
        );
        return {
          ...box,
          box_format:
            mapping?.cherry_box_format ||
            (isValidBoxFormat(box.box_format)
              ? normalizeBoxValue(box.box_format)
              : 'NO ENCONTRADO'),
        };
      });

      const liquidatedDetailIndex = withLiquidatedDetails
        ? buildLiquidatedDetailIndex(
            (await getSettlementsByExporterId(
              exporterId,
              'approved',
              selectedSeason,
              selectedSpecies.id,
              'all',
              ['*'],
              currentDivisionId()
            )) || []
          )
        : null;

      // Sheet: detalle de cajas por temporada/especie (todos los registros cargados en la vista)
      const boxesData = exportShipments.map((b) => ({
        Temporada: b.season,
        Especie: b.species_id,
        Referencia: b.reference_number,
        Cliente: b.receiver,
        Productor: b.producer_name || '',
        Packing: b.packing_name,
        Contenedor: b.container_number,
        Nave: b.ship_name,
        TipoNave: b.ship_type,
        PuertoOrigen: b.ship_origin_port,
        PuertoDestino: b.ship_destination_port,
        PaisOrigen: b.ship_origin_country,
        PaisDestino: b.ship_destination_country,
        ETD: b.ship_etd ? formatDate(b.ship_etd, 'yyyy-MM-dd') : '',
        ETA: b.ship_eta ? formatDate(b.ship_eta, 'yyyy-MM-dd') : '',
        SemanaETD: b.ship_etd ? getWeekKey(b.ship_etd) || '' : '',
        SemanaETA: b.ship_eta ? getWeekKey(b.ship_eta) || '' : '',
        'Variedad Real': b.variety,
        'Variedad Etiquetada': b.labeled_variety,
        Calibre: b.caliber || '',
        Color: b.color || '',
        Formato: b.box_format || '',
        Etiqueta: b.box_label,
        TipoBulto: b.box_type || '',
        PesoCaja: b.box_weight,
        Cantidad: b.quantity,
        Pallet: b.pallet,
        Categoria: b.category || '',
        Lote: b.lot || '',
        FechaRecepcion: b.reception_date
          ? formatDate(b.reception_date, 'yyyy-MM-dd')
          : '',
        NroProceso: b.process_internal_id || '',
        FechaProceso: b.process_date
          ? formatDate(b.process_date, 'yyyy-MM-dd')
          : '',
        'Fecha Despacho Packing': b.packing_etd
          ? formatDate(b.packing_etd, 'yyyy-MM-dd')
          : '',
        'Tipo de Negocio': b.business_type || '',
        'Orden de Venta': b.sale_order || '',
        Incoterm: b.original_incoterm || '',
        'Precio FOB': b.fob_price ?? '',
        PlantaProceso: b.packing_name || '',
        RUTProductor: b.producer_national_id || '',
        GrupoProductor: b.producer_group || '',
        CodigoSAG: b.producer_sag_code || '',
        DescripcionEmbalaje: b.box_description || '',
        'Factura SII': b.invoice_number || '',
        'Región de Venta': b.sale_region || '',
        'Mercado de Venta': b.sale_market || '',
        'Segregación Fruta': b.fruit_segregation || '',
        'Tipo Flete': b.freight_type || '',
        'Tipo de Frío': b.cold_type || '',
        Booking: b.booking_number || b.booking_awb || '',
        AWB: b.awb_number || '',
        ...(liquidatedDetailIndex
          ? buildLiquidatedDetailColumns(b, liquidatedDetailIndex)
          : {}),
      }));

      const boxesSheet = XLSX.utils.json_to_sheet(boxesData);
      // Preserve numeric precision for sums; round only the Excel display.
      Object.keys(boxesData[0] || {}).forEach((column, columnIndex) => {
        if (
          !column.includes('Liquidado') &&
          !column.includes('Liquidada') &&
          column !== 'Tipo de Cambio Liquidación'
        )
          return;
        const numberFormat =
          column.includes('/kg') || column === 'Tipo de Cambio Liquidación'
            ? '0.0000'
            : '#,##0.00';
        boxesData.forEach((_, rowIndex) => {
          const cell =
            boxesSheet[
              XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex })
            ];
          if (cell?.t === 'n') cell.z = numberFormat;
        });
      });
      const workbook = XLSX.utils.book_new();
      // Append Cajas FIRST so it becomes the primary sheet
      XLSX.utils.book_append_sheet(workbook, boxesSheet, 'Cajas');

      // Sheet: resumen por referencia (aplica filtros actuales)
      const dataToExport = filteredShipments.map((shipment) => ({
        Estado: shipment.status === 'settled' ? 'Liquidado' : 'Pendiente',
        Cliente: shipment.receiver || '-',
        Referencia: shipment.reference_number,
        Nave: shipment.ship_name,
        ETD: shipment.ship_etd
          ? formatDate(shipment.ship_etd, 'yyyy-MM-dd')
          : '-',
        ETA: shipment.ship_eta
          ? formatDate(shipment.ship_eta, 'yyyy-MM-dd')
          : '-',
        'Fecha estimada de liquidación':
          shipment.status !== 'settled' && shipment.estimated_settlement_date
            ? formatDate(
                (shipment.estimated_settlement_date as Date).toISOString(),
                'yyyy-MM-dd'
              )
            : '-',
      }));
      const summarySheet = XLSX.utils.json_to_sheet(dataToExport);
      // Append Resumen SECOND
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

      XLSX.writeFile(
        workbook,
        withLiquidatedDetails
          ? 'despachos_detalle_liquidado.xlsx'
          : 'despachos.xlsx'
      );
    } catch (error) {
      console.error('Error exporting shipments:', error);
      toast.error(t('Error al exportar despachos'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <p className='text-sm text-default-500'>{t('Filtros')}</p>
            <SeasonFilter size='sm' />
            <Input
              isClearable
              size='lg'
              placeholder={t('Por referencia...')}
              startContent={<Icon icon='tabler:search' />}
              value={searchTerm}
              onClear={() => setSearchTerm('')}
              onValueChange={setSearchTerm}
              radius='sm'
            />
            <Input
              isClearable
              size='lg'
              placeholder={t('Por contenedor o AWB...')}
              startContent={<Icon icon='tabler:search' />}
              value={trackingIdentifierSearch}
              onClear={() => setTrackingIdentifierSearch('')}
              onValueChange={setTrackingIdentifierSearch}
              radius='sm'
            />
            <Select
              size='sm'
              label={t('Cliente')}
              selectedKeys={[selectedClient]}
              onSelectionChange={(keys) =>
                setSelectedClient(Array.from(keys)[0] as string)
              }
              radius='sm'
              items={[
                { key: 'all', label: t('Todos') },
                ...uniqueClients.map((client) => ({
                  key: client,
                  label: client,
                })),
              ]}
            >
              {(item) => (
                <SelectItem key={item.key} value={item.key}>
                  {item.label}
                </SelectItem>
              )}
            </Select>
            {isMultiDivision ? (
              <Select
                size='sm'
                label={t('División')}
                selectedKeys={[selectedDivision]}
                onSelectionChange={(keys) =>
                  setSelectedDivision(Array.from(keys)[0] as string)
                }
                radius='sm'
                items={[
                  { key: 'all', label: t('Todos') },
                  ...divisions.map((division) => ({
                    key: String(division.id),
                    label: division.name,
                  })),
                ]}
              >
                {(item) => (
                  <SelectItem key={item.key} value={item.key}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            ) : null}
            <Select
              size='sm'
              label={t('Estado Liquidación')}
              selectedKeys={[selectedStatus]}
              onSelectionChange={(keys) =>
                setSelectedStatus(Array.from(keys)[0] as SettlementStatusFilter)
              }
              radius='sm'
            >
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>
              <SelectItem key='settled' value='settled'>
                {t('Liquidado')}
              </SelectItem>
              <SelectItem key='pending' value='pending'>
                {t('Pendiente')}
              </SelectItem>
              <SelectItem key='overdue' value='overdue'>
                {t('Vencida')}
              </SelectItem>
            </Select>
            <Select
              size='sm'
              label={t('Estado Logística')}
              selectedKeys={[selectedLogisticsStatus]}
              onSelectionChange={(keys) =>
                setSelectedLogisticsStatus(
                  Array.from(keys)[0] as LogisticsStatusFilter
                )
              }
              radius='sm'
            >
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>
              <SelectItem key='pendingDeparture' value='pendingDeparture'>
                {t('Por despachar')}
              </SelectItem>
              <SelectItem key='inTransit' value='inTransit'>
                {t('En tránsito')}
              </SelectItem>
              <SelectItem key='arrived' value='arrived'>
                {t('Arribado')}
              </SelectItem>
              <SelectItem key='missingEta' value='missingEta'>
                {t('Sin ETA')}
              </SelectItem>
            </Select>
            <Select
              size='sm'
              label={t('Packing')}
              selectedKeys={[selectedPacking]}
              onSelectionChange={(keys) =>
                setSelectedPacking(Array.from(keys)[0] as string)
              }
              radius='sm'
              items={[
                { key: 'all', label: t('Todos') },
                ...uniquePackings.map((packing) => ({
                  key: packing,
                  label: packing,
                })),
              ]}
            >
              {(item) => (
                <SelectItem key={item.key} value={item.key}>
                  {item.label}
                </SelectItem>
              )}
            </Select>
            <Select
              size='sm'
              label={t('Trackeado vía API')}
              selectedKeys={[selectedApiTracking]}
              onSelectionChange={(keys) =>
                setSelectedApiTracking(Array.from(keys)[0] as ApiTrackingFilter)
              }
              radius='sm'
            >
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>
              <SelectItem key='tracked' value='tracked'>
                {t('Trackeado')}
              </SelectItem>
              <SelectItem key='untracked' value='untracked'>
                {t('No trackeado')}
              </SelectItem>
            </Select>
            <div className='space-y-2 border-t border-default-200 pt-4'>
              <p className='text-sm text-default-500'>{t('Visualización')}</p>
              <SegmentedToggle
                value={metric}
                options={[
                  { value: 'kg', label: t('Kg') },
                  { value: 'ceq', label: 'CEQ' },
                ]}
                onChange={setMetric}
              />
              <p className='text-xs text-default-400'>
                CEQ = {selectedSpecies?.default_unit_price || 5} kg
              </p>
            </div>
            <div className='space-y-2 border-t border-default-200 pt-4'>
              <Select
                size='sm'
                radius='sm'
                label={t('Fuente de datos')}
                selectedKeys={[summarySourceMode]}
                onSelectionChange={(keys) =>
                  setSummarySourceMode(Array.from(keys)[0] as ChartSourceMode)
                }
              >
                <SelectItem key='effective' value='effective'>
                  {t('Trackeo + Base Despachos')}
                </SelectItem>
                <SelectItem key='canonical' value='canonical'>
                  {t('Solo Base Despachos')}
                </SelectItem>
              </Select>
              <p className='text-xs text-default-400'>
                {summarySourceMode === 'effective'
                  ? t('Prioriza trackeo y usa Base Despachos como respaldo')
                  : t('Ignora datos de trackeo en los gráficos')}
              </p>
            </div>
            <div className='space-y-3 border-t border-default-200 pt-4'>
              <div className='flex items-center justify-between gap-2'>
                <p className='text-sm text-default-500'>
                  {t('Filtros Adicionales')}
                </p>
                {Object.values(selectedDimensions).some(Boolean) ? (
                  <Button
                    size='sm'
                    variant='light'
                    onPress={() => setSelectedDimensions({})}
                  >
                    {t('Limpiar')}
                  </Button>
                ) : null}
              </div>
              {dimensionOptions.map((dimension) => (
                <Select
                  key={dimension.id}
                  size='sm'
                  radius='sm'
                  label={dimension.title}
                  selectedKeys={[selectedDimensions[dimension.id] || 'all']}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0] as string | undefined;
                    setSelectedDimensions((current) => ({
                      ...current,
                      [dimension.id]: key && key !== 'all' ? key : '',
                    }));
                  }}
                  items={[
                    { key: 'all', label: t('Todos') },
                    ...dimension.options,
                  ]}
                >
                  {(item) => (
                    <SelectItem key={item.key}>{item.label}</SelectItem>
                  )}
                </Select>
              ))}
            </div>
          </div>
        </div>
      </ModalSlideFilter>
      <PageLayout>
        <Header
          actions={
            <>
              {usesFrutic && (
                <>
                  <Button
                    variant='flat'
                    isLoading={isSourceSyncing}
                    isDisabled={loading}
                    startContent={<Icon icon='tabler:refresh' />}
                    onPress={() => fetchData('sync')}
                  >
                    {t('Actualizar desde Frutic')}
                  </Button>
                  {pendingReplacements.length > 0 && (
                    <Button
                      variant='flat'
                      onPress={() => setReviewingReplacements(true)}
                    >
                      {t('Revisar reemplazos')} ({pendingReplacements.length})
                    </Button>
                  )}
                </>
              )}
              {!usesFrutic && (
                <Button
                  color='danger'
                  variant='light'
                  onPress={onBulkDeleteModalOpen}
                  startContent={<Icon icon='tabler:trash' />}
                >
                  {t('Borrar')}
                </Button>
              )}
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    variant='flat'
                    isLoading={isExporting}
                    startContent={<Icon icon='tabler:download' />}
                    endContent={
                      <Icon icon='tabler:chevron-down' className='text-xs' />
                    }
                  >
                    {t('Exportar')}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label='Opciones de exportación de despachos'
                  onAction={(key) =>
                    handleExportShipments(key === 'with-liquidated-details')
                  }
                >
                  <DropdownItem
                    key='standard'
                    startContent={<Icon icon='tabler:download' />}
                  >
                    {t('Exportar Despachos')}
                  </DropdownItem>
                  <DropdownItem
                    key='with-liquidated-details'
                    startContent={<Icon icon='tabler:download' />}
                    description={t(
                      'Considera despachos liquidados; no considera liquidaciones que no aparezcan despachadas.'
                    )}
                  >
                    {t('Exportar Despachos Con Detalle Liquidado')}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              {!usesFrutic && (
                <Button
                  color='primary'
                  onPress={onOpen}
                  startContent={<Icon icon='tabler:upload' />}
                >
                  {t('Subir')}
                </Button>
              )}
            </>
          }
          hasFrutic={usesFrutic}
          title={`${t(viewMode === 'resumen' ? 'Resumen' : 'Detalle')} ${t('Despachos')}`}
          description={
            usesFrutic
              ? t(
                  'Visualiza tu planilla de despachos para controlar despachos liquidados, calcular preliquidaciones a productores y facilitar la liquidación manual de algunos de ellos.'
                )
              : t(
                  'Carga tu planilla de despachos para controlar despachos liquidados, calcular preliquidaciones a productores y facilitar la liquidación manual de algunos de ellos.'
                )
          }
        />

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>

        {viewMode === 'resumen' ? (
          loading ? (
            <LoadingState label={t('Cargando despachos...')} />
          ) : (
            <div className='space-y-4 mt-4'>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)_minmax(0,1.3fr)]'>
                <Card
                  shadow='sm'
                  className='min-w-0 md:col-span-2 xl:col-span-1'
                >
                  <CardBody className='flex min-h-[208px] flex-col justify-center gap-3 p-6'>
                    <h3 className='text-xs font-semibold uppercase tracking-wider text-default-500'>
                      {t('Total despachado')}
                    </h3>
                    <p className='text-2xl font-semibold text-default-900'>
                      {formatSummaryNumber(
                        shipmentSummaryMetrics.total[metric]
                      )}{' '}
                      <span className='whitespace-nowrap'>{unitLabel}</span>
                    </p>
                    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-default-500'>
                      <span>
                        <span className='font-semibold text-default-800'>
                          {formatSummaryNumber(
                            shipmentSummaryMetrics.total[
                              metric === 'kg' ? 'ceq' : 'kg'
                            ]
                          )}
                        </span>{' '}
                        {metric === 'kg' ? 'CEQ' : 'kg'}
                      </span>
                      <span>
                        <span className='font-semibold text-default-800'>
                          {formatSummaryNumber(
                            shipmentSummaryMetrics.total.references,
                            0
                          )}
                        </span>{' '}
                        {t('referencias')}
                      </span>
                    </div>
                  </CardBody>
                </Card>

                {[
                  {
                    title: t('Logística'),
                    cards: logisticsCards,
                    badge:
                      shipmentSummaryMetrics.logistics.missingEta.references > 0
                        ? `${t('Sin ETA')}: ${formatSummaryNumber(shipmentSummaryMetrics.logistics.missingEta.references, 0)} ${t('ref')}`
                        : null,
                    badgeTitle: `${formatSummaryNumber(shipmentSummaryMetrics.logistics.missingEta[metric])} ${unitLabel}`,
                  },
                  {
                    title: t('Liquidación'),
                    cards: settlementCards,
                    badge: `${t('Vencida')}: ${t('> 30 días ETA')}`,
                    badgeTitle: undefined,
                  },
                ].map((section) => (
                  <Card key={section.title} shadow='sm' className='min-w-0'>
                    <CardBody className='gap-4 p-6'>
                      <div className='flex min-h-6 flex-wrap items-center justify-between gap-2'>
                        <h3 className='text-base font-semibold text-default-900'>
                          {section.title}
                        </h3>
                        {section.badge ? (
                          <span
                            title={section.badgeTitle}
                            className='rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold leading-none text-amber-700 dark:text-amber-400'
                          >
                            {section.badge}
                          </span>
                        ) : null}
                      </div>
                      <div
                        aria-hidden='true'
                        className='flex h-3 shrink-0 overflow-hidden rounded-full bg-default-200/70'
                      >
                        {section.cards
                          .filter((card) => card.progressValue > 0)
                          .map((card) => (
                            <div
                              key={card.key}
                              className={`h-full shrink-0 border-r-2 border-content1 last:border-r-0 ${card.progressClassName}`}
                              style={{
                                width: `${Math.min(card.progressValue, 100)}%`,
                              }}
                            />
                          ))}
                      </div>
                      <ul className='space-y-2'>
                        {section.cards.map((card) => (
                          <li
                            key={card.key}
                            className='flex items-center gap-2 text-sm'
                          >
                            <span
                              className={`h-2.5 w-2.5 shrink-0 rounded-full ${card.dotClassName}`}
                            />
                            <span className='min-w-0 flex-1 text-default-500'>
                              {card.title} ·{' '}
                              {formatSummaryNumber(card.metric.references, 0)}{' '}
                              {t('ref')}
                            </span>
                            <span
                              title={`${formatSummaryNumber(card.metric.kg)} kg · ${formatSummaryNumber(card.metric.ceq)} CEQ`}
                              className='shrink-0 whitespace-nowrap font-mono font-semibold tabular-nums text-default-800'
                            >
                              {formatSummaryVolume(card.metric[metric])}{' '}
                              {unitLabel}
                            </span>
                            <span className='w-14 shrink-0 text-right font-mono tabular-nums text-default-500'>
                              {formatSummaryNumber(card.progressValue)}%
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardBody>
                  </Card>
                ))}
              </div>

              <div className='grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]'>
                <Card shadow='sm' className='min-w-0'>
                  <CardBody className='gap-6 p-6'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                      <div>
                        <h2 className='text-lg font-semibold text-default-900'>
                          {t('Distribución del despacho')}
                        </h2>
                        <p className='text-sm text-default-500'>
                          {t('Participación por dimensión, en {{unit}}', {
                            unit: unitLabel,
                          })}
                        </p>
                      </div>
                    </div>
                    {distributionSections.map((section) => (
                      <section
                        key={section.id}
                        aria-labelledby={`summary-${section.id}`}
                        className='space-y-3'
                      >
                        <div className='flex items-center gap-2.5'>
                          <span
                            className='h-2.5 w-2.5 shrink-0 rounded-sm'
                            style={{ backgroundColor: section.color }}
                          />
                          <h3
                            id={`summary-${section.id}`}
                            className='text-xs font-semibold uppercase tracking-wider text-default-500'
                          >
                            {section.title}
                          </h3>
                          <div className='h-px flex-1 bg-default-200/70' />
                        </div>
                        {section.rows.map((row) => (
                          <ShipmentDistributionRow
                            key={row.id}
                            title={row.title}
                            entries={row.entries}
                            color={section.color}
                            unitLabel={unitLabel}
                            preserveOrder={
                              row.id === 'caliber' &&
                              caliberOrder === 'ascending'
                            }
                            rowControls={
                              'rowControls' in row ? row.rowControls : undefined
                            }
                            controls={
                              'controls' in row ? row.controls : undefined
                            }
                            description={
                              'description' in row ? row.description : undefined
                            }
                          />
                        ))}
                      </section>
                    ))}
                  </CardBody>
                </Card>
                <div className='min-w-0 space-y-5'>
                  <Card shadow='sm' className='min-w-0'>
                    <CardBody className='p-6'>
                      <div className='mb-3 flex min-h-9 flex-wrap items-center justify-between gap-2 pl-1'>
                        <h3 className='text-base font-semibold'>
                          {t('Por Semana')}
                          <span className='ml-2 text-xs font-normal text-default-500'>
                            {unitLabel} · {weekDateMode.toUpperCase()}
                          </span>
                        </h3>
                        <div className='flex flex-wrap items-center justify-end gap-2'>
                          <SegmentedToggle
                            compact
                            value={weekDateMode}
                            options={[
                              { value: 'etd', label: 'ETD' },
                              { value: 'eta', label: 'ETA' },
                            ]}
                            onChange={setWeekDateMode}
                          />
                          {renderSourceCoverageChip(
                            weekVolumeSeries.coverage,
                            t('{{field}} desde API', {
                              field: weekDateMode.toUpperCase(),
                            }),
                            t('usan fecha vigente de API', {
                              field: weekDateMode.toUpperCase(),
                            })
                          )}
                          {weekVolumeSeries.missingDateReferences > 0 ? (
                            <Chip
                              size='sm'
                              variant='flat'
                              color={
                                weekVolumeSeries.missingDateReferences > 0
                                  ? 'warning'
                                  : 'default'
                              }
                            >
                              {weekDateMode === 'eta'
                                ? t('Sin ETA')
                                : t('Sin ETD')}{' '}
                              ·{' '}
                              {formatNumber(
                                weekVolumeSeries.missingDateReferences
                              )}
                            </Chip>
                          ) : null}
                        </div>
                      </div>
                      <ReactApexChart
                        options={weekVolumeSeries.options}
                        series={weekVolumeSeries.series as any}
                        type='bar'
                        height={300}
                      />
                    </CardBody>
                  </Card>
                  <Card shadow='sm' className='min-w-0'>
                    <CardBody className='gap-4 p-6'>
                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <h2 className='text-base font-semibold'>
                          {t('Por Cliente')}
                        </h2>
                        {unmappedReceivers.length > 0 ? (
                          <button
                            type='button'
                            onClick={onReceiverMappingModalOpen}
                            className='rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 dark:text-amber-400'
                          >
                            {unmappedReceivers.length}{' '}
                            {t('clientes no mapeados')}
                          </button>
                        ) : null}
                      </div>
                      <ShipmentDistributionRow
                        title={t('Por Cliente')}
                        entries={receiverDistribution}
                        color='#58c66b'
                        unitLabel={unitLabel}
                        variant='ranking'
                      />
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          )
        ) : null}

        {viewMode === 'detalle' ? (
          <div className='space-y-3'>
            {unmappedReceivers.length > 0 ? (
              <button
                type='button'
                onClick={onReceiverMappingModalOpen}
                className='flex w-full items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 dark:hover:bg-amber-500/15'
              >
                <span className='font-medium'>
                  {unmappedReceivers.length} {t('clientes no mapeados')}
                </span>
                <span className='text-xs font-semibold text-amber-700 dark:text-amber-300'>
                  {t('Mapear clientes')}
                </span>
              </button>
            ) : null}

            <Table
              aria-label={t('Tabla de despachos')}
              bottomContent={
                <div className='flex w-full items-center justify-between border-t border-divider px-5 py-3'>
                  <span className='text-xs text-default-500'>
                    {sortedShipments.length} {t('despachos')}
                  </span>
                  {pages > 1 ? (
                    <Pagination
                      isCompact
                      showControls
                      showShadow
                      color='primary'
                      page={page}
                      total={pages}
                      onChange={(page) => setPage(page)}
                    />
                  ) : null}
                </div>
              }
              classNames={{
                wrapper:
                  'gap-0 overflow-x-auto rounded-xl border border-divider bg-content1 p-0 shadow-none',
                table: 'min-w-[980px]',
                th: 'h-14 rounded-none border-b border-divider bg-content1 px-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-default-500 first:rounded-none last:rounded-none',
                td: 'border-b border-divider px-5 py-4 align-middle group-last:border-b-0',
              }}
            >
              <TableHeader>
                <TableColumn key='shipment_summary' className='w-[27%]'>
                  <ShipmentSortableHeader
                    label={`${t('Cliente')} / ${t('Referencia')}`}
                    options={shipmentSortGroups.shipment}
                    sort={shipmentSort}
                    operationalOrderLabel={t('Orden operativo')}
                    menuLabel={t('Ordenar columna')}
                    onSelect={handleShipmentSortSelect}
                  />
                </TableColumn>
                <TableColumn key='container_summary' className='w-[18%]'>
                  <ShipmentSortableHeader
                    label={t('Contenedor')}
                    options={shipmentSortGroups.container}
                    sort={shipmentSort}
                    operationalOrderLabel={t('Orden operativo')}
                    menuLabel={t('Ordenar columna')}
                    onSelect={handleShipmentSortSelect}
                  />
                </TableColumn>
                <TableColumn key='logistics_summary' className='w-[36%]'>
                  <ShipmentSortableHeader
                    label={t('Logística')}
                    options={shipmentSortGroups.logistics}
                    sort={shipmentSort}
                    operationalOrderLabel={t('Orden operativo')}
                    menuLabel={t('Ordenar columna')}
                    onSelect={handleShipmentSortSelect}
                  />
                </TableColumn>
                <TableColumn key='settlement_summary' className='w-[19%]'>
                  <ShipmentSortableHeader
                    label={t('Liquidación')}
                    options={shipmentSortGroups.settlement}
                    sort={shipmentSort}
                    operationalOrderLabel={t('Orden operativo')}
                    menuLabel={t('Ordenar columna')}
                    onSelect={handleShipmentSortSelect}
                  />
                </TableColumn>
              </TableHeader>
              <TableBody
                items={paginatedShipments}
                isLoading={loading}
                loadingContent={<Spinner label={t('Cargando...')} />}
                emptyContent={t('No se encontraron despachos')}
              >
                {(item) => (
                  <TableRow
                    key={item.id}
                    className='group transition-colors hover:bg-default-100/50'
                  >
                    {(columnKey) => (
                      <TableCell>{renderCell(item, columnKey)}</TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        ) : null}

        <FormatMappingModal
          isOpen={isFormatMappingModalOpen}
          onClose={onFormatMappingModalClose}
          speciesId={selectedSpecies?.id}
          season={selectedSeason}
        />

        <Modal
          isOpen={isReceiverMappingModalOpen}
          onClose={handleCloseReceiverMappingModal}
          placement='center'
          size='5xl'
          scrollBehavior='inside'
        >
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className='flex flex-col gap-1'>
                  <h3 className='text-xl font-semibold'>
                    {t('Mapeo de Clientes')}
                  </h3>
                  <p className='text-sm font-normal text-default-500'>
                    {unmappedReceivers.length} {t('clientes no mapeados')}
                  </p>
                </ModalHeader>
                <ModalBody>
                  {selectedSpecies?.id && selectedSeason ? (
                    <ReceiverMappingSection
                      speciesId={selectedSpecies.id}
                      season={selectedSeason}
                      hide={false}
                      maxColumns={2}
                    />
                  ) : null}
                </ModalBody>
                <ModalFooter>
                  <Button variant='light' onPress={onClose}>
                    {t('Cerrar')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        <Modal
          isOpen={isDemoModalOpen}
          onClose={onDemoModalClose}
          placement='center'
        >
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className='flex flex-col gap-1'>
                  <h3 className='text-xl font-semibold'>
                    {t('Solicita una demo')}
                  </h3>
                </ModalHeader>
                <ModalBody>
                  <p className='text-default-600'>
                    {t('Escribe a')}{' '}
                    <a
                      href='mailto:vicente@cherrymarket.cl'
                      className='text-primary font-medium hover:underline'
                    >
                      vicente@cherrymarket.cl
                    </a>{' '}
                    {t('o llama a')}{' '}
                    <a
                      href='tel:+56984408110'
                      className='text-primary font-medium hover:underline'
                    >
                      +56984408110
                    </a>{' '}
                    {t(
                      'para hacerte una demo de nuestro servicio de Portal de Productores.'
                    )}
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button color='primary' onPress={onClose}>
                    {t('Entendido')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        <ExcelUploadModal
          isOpen={isOpen}
          onClose={onClose}
          onUploadComplete={handleUploadComplete}
        />

        {/* Bulk Delete Confirmation Modal */}
        <Modal
          isOpen={isBulkDeleteModalOpen}
          onClose={() => {
            setBulkDeletePacking('');
            onBulkDeleteModalClose();
          }}
          placement='center'
        >
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className='flex flex-col gap-1'>
                  <h3 className='text-xl font-semibold text-danger'>
                    {t('Confirmar Eliminación Masiva')}
                  </h3>
                </ModalHeader>
                <ModalBody>
                  <p className='text-default-600 mb-4'>
                    {t('Seleccione el packing del cual desea eliminar')}{' '}
                    <strong className='text-danger'>
                      {t('todos los despachos')}
                    </strong>
                    :
                  </p>

                  <Select
                    size='md'
                    label={t('Packing')}
                    placeholder={t('Seleccione un packing')}
                    selectedKeys={[bulkDeletePacking]}
                    onSelectionChange={(keys) => {
                      console.log(Array.from(keys));
                      setBulkDeletePacking(Array.from(keys)[0] as string);
                    }}
                    radius='sm'
                    isRequired
                  >
                    {uniquePackings.map((packing) => (
                      <SelectItem
                        key={packing}
                        value={packing}
                        textValue={packing}
                      >
                        {packing} (
                        {countShipmentBoxRows(
                          shipments.filter(
                            (box) => box.packing_name === packing
                          )
                        )}{' '}
                        {t('bultos')})
                      </SelectItem>
                    ))}
                  </Select>

                  {bulkDeletePacking && (
                    <div className='mt-4 p-3 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200'>
                      <p className='text-default-600 text-sm mb-2'>
                        {t('Se eliminarán')}{' '}
                        <strong className='text-danger'>
                          {countShipmentBoxRows(
                            shipments.filter(
                              (box) => box.packing_name === bulkDeletePacking
                            )
                          )}{' '}
                          {t('bultos')}
                        </strong>{' '}
                        {t('del packing')}{' '}
                        <strong className='text-danger'>
                          "{bulkDeletePacking}"
                        </strong>{' '}
                        {t('de la temporada')} {selectedSeason}.
                      </p>
                      <p className='text-danger text-xs font-semibold'>
                        ⚠️ {t('Esta acción no se puede deshacer.')}
                      </p>
                    </div>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant='light'
                    onPress={() => {
                      setBulkDeletePacking('');
                      onClose();
                    }}
                  >
                    {t('Cancelar')}
                  </Button>
                  <Button
                    color='danger'
                    onPress={handleBulkDeleteByPacking}
                    isLoading={isBulkDeleting}
                    isDisabled={!bulkDeletePacking}
                  >
                    {t('Eliminar Todo')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        <Modal
          isOpen={reviewingReplacements}
          onClose={() => setReviewingReplacements(false)}
        >
          <ModalContent>
            <ModalHeader>{t('Reemplazos de despachos')}</ModalHeader>
            <ModalBody>
              <p>
                {t(
                  'Frutic creó un nuevo despacho con una referencia existente. Vincúlalo solo si corresponde al mismo despacho; conservará su historial, precios e incidentes.'
                )}
              </p>
              {pendingReplacements.map((replacement) => (
                <div key={replacement.id} className='flex flex-col gap-2 py-2'>
                  <strong>{replacement.reference}</strong>
                  <Button
                    color='warning'
                    isDisabled={loading}
                    onPress={() => {
                      setReviewingReplacements(false);
                      fetchData('accept_replacement', replacement.id);
                    }}
                  >
                    {t('Vincular reemplazo y conservar historial')}
                  </Button>
                </div>
              ))}
            </ModalBody>
          </ModalContent>
        </Modal>
        {/* Shipment Details Modal */}
        <ShipmentDetailsModal
          {...details.modalProps}
          divisions={divisions}
          receiverOptions={exporterReceivers}
          onCreateReceiver={handleCreateReceiver}
          getReceiverLabel={getDisplayReceiverName}
          priceUser={
            userProfile?.id && canRegisterPrices
              ? {
                  id: userProfile.id,
                  name: [userProfile.first_name, userProfile.last_name]
                    .filter(Boolean)
                    .join(' '),
                }
              : undefined
          }
        />
      </PageLayout>
    </div>
  );
};

export default ShipmentsPage;
